/**
 * Domain 4: Verbal & Linguistic Reasoning (الاستدلال اللفظي والدلالي)
 * 8 Modular Template Families for semantic analogies, deductive syllogisms, and categorical logic
 * Perfectly balanced bilingual English and natural Arabic.
 * Measures pure fluid relational intelligence, categorical abstraction, and deductive validity (Gf).
 */

export const VERBAL_TEMPLATES = [
  // 1. Classical Semantic & Relational Analogy (A : B :: C : ?)
  {
    id: 'verbal_analogy_relationship',
    domain: 'verbal_reasoning',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 30,
    generate(prng, difficulty = 2) {
      const analogies = [
        {
          pair1_ar: ['سفينة', 'ربان'],
          pair1_en: ['Ship', 'Captain'],
          itemC_ar: 'طائرة',
          itemC_en: 'Airplane',
          correct_ar: 'طيار',
          correct_en: 'Pilot',
          distractors_ar: ['مطار', 'جناح', 'مسافر'],
          distractors_en: ['Airport', 'Wing', 'Passenger'],
          rel_ar: 'علاقة وسيلة النقل بمن يقودها ويدير حركتها',
          rel_en: 'Relationship between transport vehicle and designated operator'
        },
        {
          pair1_ar: ['غليان', 'تبخر'],
          pair1_en: ['Boiling', 'Evaporation'],
          itemC_ar: 'تبريد',
          itemC_en: 'Freezing',
          correct_ar: 'تجمد',
          correct_en: 'Solidification',
          distractors_ar: ['دفء', 'ثلج', 'ذوبان'],
          distractors_en: ['Warmth', 'Ice', 'Melting'],
          rel_ar: 'علاقة السبب بالنتيجة الفيزيائية المباشرة لتغير الحالة',
          rel_en: 'Cause to direct physical state transformation'
        },
        {
          pair1_ar: ['بوصلة', 'اتجاه'],
          pair1_en: ['Compass', 'Direction'],
          itemC_ar: 'ساعة',
          itemC_en: 'Clock',
          correct_ar: 'وقت',
          correct_en: 'Time',
          distractors_ar: ['عقارب', 'جدار', 'سرعة'],
          distractors_en: ['Hands', 'Wall', 'Speed'],
          rel_ar: 'علاقة أداة القياس بالكمية المحددة التي تقيسها',
          rel_en: 'Measurement instrument to its fundamental measured dimension'
        },
        {
          pair1_ar: ['كتاب', 'فصل'],
          pair1_en: ['Book', 'Chapter'],
          itemC_ar: 'سنة',
          itemC_en: 'Year',
          correct_ar: 'شهر',
          correct_en: 'Month',
          distractors_ar: ['يوم', 'قرن', 'تقويم'],
          distractors_en: ['Day', 'Century', 'Calendar'],
          rel_ar: 'علاقة الكل بالوحدة التقسيمية المباشرة المكونة له',
          rel_en: 'Whole to its direct constituent sectional unit'
        },
        {
          pair1_ar: ['مجهر', 'تكبير'],
          pair1_en: ['Microscope', 'Magnify'],
          itemC_ar: 'تلسكوب',
          itemC_en: 'Telescope',
          correct_ar: 'تقريب الأجرام البعيدة',
          correct_en: 'Observe distant bodies',
          distractors_ar: ['عتمة', 'انكسار الضوء', 'صوت'],
          distractors_en: ['Darkness', 'Refraction', 'Sound'],
          rel_ar: 'علاقة الجهاز البصري بوظيفته الاستكشافية الأساسية',
          rel_en: 'Optical device to its specific observational function'
        },
        {
          pair1_ar: ['احتكاك', 'حرارة'],
          pair1_en: ['Friction', 'Heat'],
          itemC_ar: 'جهد كهربائي',
          itemC_en: 'Voltage',
          correct_ar: 'تيار كهربائي',
          correct_en: 'Electric current',
          distractors_ar: ['عزل', 'سكون', 'مغناطيس'],
          distractors_en: ['Insulation', 'Inertia', 'Magnet'],
          rel_ar: 'علاقة القوة الدافعة بالتدفق أو الأثر الفيزيائي الناتج',
          rel_en: 'Driving force to resulting flow or physical phenomenon'
        },
        {
          pair1_ar: ['نحات', 'إزميل'],
          pair1_en: ['Sculptor', 'Chisel'],
          itemC_ar: 'نجار',
          itemC_en: 'Carpenter',
          correct_ar: 'منشار',
          correct_en: 'Saw',
          distractors_ar: ['خشب', 'طاولة', 'غابة'],
          distractors_en: ['Wood', 'Table', 'Forest'],
          rel_ar: 'علاقة الحرفي بالأداة اليدوية التشكيلية المباشرة لمهنته',
          rel_en: 'Artisan to their direct handcrafting tool'
        },
        {
          pair1_ar: ['سيمفونية', 'نغمات'],
          pair1_en: ['Symphony', 'Notes'],
          itemC_ar: 'برمجية',
          itemC_en: 'Software',
          correct_ar: 'أكواد برمجية',
          correct_en: 'Source code',
          distractors_ar: ['شاشة', 'حاسوب', 'كهرباء'],
          distractors_en: ['Screen', 'Computer', 'Electricity'],
          rel_ar: 'علاقة البناء المركب بالوحدات التأسيسية التي تكونه',
          rel_en: 'Complex composite creation to its fundamental instructional units'
        },
        {
          pair1_ar: ['لقاح', 'مناعة'],
          pair1_en: ['Vaccine', 'Immunity'],
          itemC_ar: 'تمرين',
          itemC_en: 'Exercise',
          correct_ar: 'لياقة بدنية',
          correct_en: 'Physical fitness',
          distractors_ar: ['ملعب', 'عرق', 'إرهاق'],
          distractors_en: ['Stadium', 'Sweat', 'Fatigue'],
          rel_ar: 'علاقة الممارسة أو المحفز بالقدرة الحيوية المكتسبة عنه',
          rel_en: 'Stimulus/practice to acquired biological capacity'
        },
        {
          pair1_ar: ['تمدد', 'انكماش'],
          pair1_en: ['Expansion', 'Contraction'],
          itemC_ar: 'صعود',
          itemC_en: 'Ascent',
          correct_ar: 'هبوط',
          correct_en: 'Descent',
          distractors_ar: ['قمة', 'ارتفاع', 'سرعة'],
          distractors_en: ['Peak', 'Altitude', 'Speed'],
          rel_ar: 'علاقة التضاد القطبي في الحركة أو الحالة الفيزيائية',
          rel_en: 'Direct polar antonym relation along the same vector'
        },
        {
          pair1_ar: ['ميزان', 'كتلة'],
          pair1_en: ['Balance', 'Mass'],
          itemC_ar: 'بارومتر',
          itemC_en: 'Barometer',
          correct_ar: 'ضغط جوي',
          correct_en: 'Atmospheric pressure',
          distractors_ar: ['رطوبة', 'رياح', 'حرارة'],
          distractors_en: ['Humidity', 'Wind', 'Temperature'],
          rel_ar: 'أداة القياس المتخصصة بالنسبة للمتغير الفيزيائي المباشر',
          rel_en: 'Specialized scientific instrument to its physical quantity'
        },
        {
          pair1_ar: ['بذرة', 'شجرة'],
          pair1_en: ['Seed', 'Tree'],
          itemC_ar: 'فكرة',
          itemC_en: 'Idea',
          correct_ar: 'مشروع مبتكر',
          correct_en: 'Inventive project',
          distractors_ar: ['عقل', 'ورقة', 'تفكير'],
          distractors_en: ['Mind', 'Paper', 'Thinking'],
          rel_ar: 'علاقة النواة الأولية بالكيان المتكامل المكتمل النمو الناتج عنها',
          rel_en: 'Foundational origin to fully realized mature entity'
        },
        {
          pair1_ar: ['محفز', 'تفاعل'],
          pair1_en: ['Catalyst', 'Reaction'],
          itemC_ar: 'حافز تشجيعي',
          itemC_en: 'Incentive',
          correct_ar: 'إنتاجية',
          correct_en: 'Productivity',
          distractors_ar: ['مكافأة', 'مدير', 'تعب'],
          distractors_en: ['Bonus', 'Manager', 'Fatigue'],
          rel_ar: 'علاقة العنصر المسرع بالعملية التي يزيد من كفاءتها وسرعتها',
          rel_en: 'Accelerating agent to the process whose rate it optimizes'
        },
        {
          pair1_ar: ['ديسيبل', 'صوت'],
          pair1_en: ['Decibel', 'Sound intensity'],
          itemC_ar: 'لومن',
          itemC_en: 'Lumen',
          correct_ar: 'تدفق ضوئي',
          correct_en: 'Luminous flux',
          distractors_ar: ['لون', 'شمس', 'مصباح'],
          distractors_en: ['Color', 'Sun', 'Lamp'],
          rel_ar: 'وحدة القياس القياسية بالنسبة للظاهرة الفيزيائية المقاسة',
          rel_en: 'Standard unit of measurement to physical wave intensity'
        },
        {
          pair1_ar: ['منشور زجاجي', 'طيف الضوء'],
          pair1_en: ['Prism', 'Light spectrum'],
          itemC_ar: 'مرشح',
          itemC_en: 'Filter',
          correct_ar: 'تنقية وفرز',
          correct_en: 'Purification & separation',
          distractors_ar: ['ماء', 'شوائب', 'رمل'],
          distractors_en: ['Water', 'Impurities', 'Sand'],
          rel_ar: 'علاقة الأداة بفرز وتفكيك المكونات إلى عناصرها الأصلية',
          rel_en: 'Mechanism to physical decomposition or filtration of components'
        },
        {
          pair1_ar: ['تشخيص', 'علاج'],
          pair1_en: ['Diagnosis', 'Treatment'],
          itemC_ar: 'فحص العطل',
          itemC_en: 'Troubleshooting',
          correct_ar: 'إصلاح العطل',
          correct_en: 'Repair',
          distractors_ar: ['أداة', 'مهندس', 'توقف'],
          distractors_en: ['Tool', 'Engineer', 'Halt'],
          rel_ar: 'الاستقصاء وتحديد المشكلة يليه التدخل التصحيحي المباشر',
          rel_en: 'Systematic identification followed by corrective intervention'
        },
        {
          pair1_ar: ['هيكل عظمي', 'جسم الإنسان'],
          pair1_en: ['Skeleton', 'Human body'],
          itemC_ar: 'هيكل إنشائي',
          itemC_en: 'Structural frame',
          correct_ar: 'مبنى معماري',
          correct_en: 'Architectural building',
          distractors_ar: ['إسمنت', 'أرض', 'مهندس'],
          distractors_en: ['Cement', 'Ground', 'Architect'],
          rel_ar: 'علاقة الدعامة الصلبة الحاملة بالكيان الكامل المحمول',
          rel_en: 'Load-bearing core framework to full exterior structure'
        },
        {
          pair1_ar: ['جفاف', 'قحط'],
          pair1_en: ['Drought', 'Aridity'],
          itemC_ar: 'فيضان',
          itemC_en: 'Flood',
          correct_ar: 'غمر مائي',
          correct_en: 'Submersion',
          distractors_ar: ['سد', 'مطر خفيف', 'سماء'],
          distractors_en: ['Dam', 'Light drizzle', 'Sky'],
          rel_ar: 'الحدث الطبيعي والنتيجة المباشرة المترتبة على حدوثه',
          rel_en: 'Natural extreme event to its defining physical outcome'
        },
        {
          pair1_ar: ['بديهية', 'برهان'],
          pair1_en: ['Axiom', 'Proof'],
          itemC_ar: 'فرضية',
          itemC_en: 'Hypothesis',
          correct_ar: 'تجربة وتحقق',
          correct_en: 'Empirical experiment',
          distractors_ar: ['كتاب', 'مختبر', 'عالم'],
          distractors_en: ['Book', 'Laboratory', 'Scientist'],
          rel_ar: 'المنطلق النظري متبوعًا بآلية التحقق الإثباتية الصارمة',
          rel_en: 'Theoretical premise to its rigorous validation method'
        },
        {
          pair1_ar: ['مرساة', 'سفينة'],
          pair1_en: ['Anchor', 'Ship'],
          itemC_ar: 'مكابح',
          itemC_en: 'Brake',
          correct_ar: 'سيارة',
          correct_en: 'Car',
          distractors_ar: ['طريق', 'عجلة', 'وقود'],
          distractors_en: ['Road', 'Wheel', 'Fuel'],
          rel_ar: 'أداة الإيقاف والتثبيت بالنسبة للمركبة المخصصة لها',
          rel_en: 'Deceleration/arrest mechanism to corresponding vehicle'
        }
      ];

      const item = prng.pick(analogies);
      const allChoices = prng.shuffle([
        { ar: item.correct_ar, en: item.correct_en, isCorrect: true },
        { ar: item.distractors_ar[0], en: item.distractors_en[0], isCorrect: false },
        { ar: item.distractors_ar[1], en: item.distractors_en[1], isCorrect: false },
        { ar: item.distractors_ar[2], en: item.distractors_en[2], isCorrect: false }
      ]);

      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-analogy-board" role="region" aria-label="تناسب لفظي">
          <div class="iq-analogy-pair">
            <span class="iq-analogy-token">${item.pair1_ar[0]}</span>
            <span class="iq-analogy-rel">:</span>
            <span class="iq-analogy-token">${item.pair1_ar[1]}</span>
          </div>
          <div class="iq-analogy-sep">::</div>
          <div class="iq-analogy-pair">
            <span class="iq-analogy-token">${item.itemC_ar}</span>
            <span class="iq-analogy-rel">:</span>
            <span class="iq-analogy-placeholder">[ ؟ ]</span>
          </div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-analogy-board" role="region" aria-label="Verbal Analogy">
          <div class="iq-analogy-pair">
            <span class="iq-analogy-token">${item.pair1_en[0]}</span>
            <span class="iq-analogy-rel">:</span>
            <span class="iq-analogy-token">${item.pair1_en[1]}</span>
          </div>
          <div class="iq-analogy-sep">::</div>
          <div class="iq-analogy-pair">
            <span class="iq-analogy-token">${item.itemC_en}</span>
            <span class="iq-analogy-rel">:</span>
            <span class="iq-analogy-placeholder">[ ? ]</span>
          </div>
        </div>
      `;

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `أكمل التناسب المنطقي: (${item.pair1_ar[0]} بالنسبة لـ ${item.pair1_ar[1]}) كما (${item.itemC_ar} بالنسبة لـ ...)؟`,
        questionText_en: `Complete the logical analogy: (${item.pair1_en[0]} is to ${item.pair1_en[1]}) as (${item.itemC_en} is to ...)?`,
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options,
        correctIndex,
        explanation_ar: `العلاقة المنطقية هنا: ${item.rel_ar}. الإجابة الصحيحة هي "${item.correct_ar}".`,
        explanation_en: `The underlying logical relationship is: ${item.rel_en}. The correct answer is "${item.correct_en}".`
      };
    }
  },

  // 2. Formal Deductive Syllogism (القياس المنطقي الصارم)
  {
    id: 'verbal_syllogism_deduction',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const syllogisms = [
        {
          premise1_ar: 'كل الفلزات موصلة للحرارة.',
          premise1_en: 'All metals are thermal conductors.',
          premise2_ar: 'النحاس فلز.',
          premise2_en: 'Copper is a metal.',
          conclusion_ar: 'النحاس موصل للحرارة.',
          conclusion_en: 'Copper is a thermal conductor.',
          distractors_ar: [
            'كل موصلات الحرارة هي نحاس.',
            'بعض الفلزات فقط توصل الحرارة.',
            'لا يمكن تحديد خصائص النحاس.'
          ],
          distractors_en: [
            'All thermal conductors are copper.',
            'Only some metals conduct heat.',
            'Copper properties cannot be determined.'
          ],
          exp_ar: 'قياس استثنائي صارم: بما أن كل الفلزات توصل الحرارة والنحاس فلز، فالنتيجة الحتمية أن النحاس موصل.',
          exp_en: 'Universal deductive syllogism: since all metals conduct heat and Copper is a metal, Copper must conduct heat.'
        },
        {
          premise1_ar: 'جميع الموظفين في القسم (أ) يتحدثون لغتين على الأقل.',
          premise1_en: 'All employees in Department A speak at least two languages.',
          premise2_ar: 'سارة موظفة في القسم (أ).',
          premise2_en: 'Sarah is an employee in Department A.',
          conclusion_ar: 'سارة تتحدث لغتين على الأقل.',
          conclusion_en: 'Sarah speaks at least two languages.',
          distractors_ar: [
            'سارة تتحدث الإنجليزية والفرنسية تحديداً.',
            'جميع متحدثي اللغتين يعملون في القسم (أ).',
            'سارة تدير القسم (أ).'
          ],
          distractors_en: [
            'Sarah speaks specifically English and French.',
            'All bilingual individuals work in Department A.',
            'Sarah manages Department A.'
          ],
          exp_ar: 'استنتاج منطقي قطعي مباشر ينبع من العمومية المحددة في المقدمة الأولى دون افتراضات زائدة.',
          exp_en: 'Deductive necessity derived strictly from universal premise without unstated assumptions.'
        },
        {
          premise1_ar: 'لا يوجد كائن من الثدييات من ذوات الدم البارد.',
          premise1_en: 'No mammal is a cold-blooded animal.',
          premise2_ar: 'الحوت الأزرق ينتمي إلى طائفة الثدييات.',
          premise2_en: 'The blue whale belongs to the class of mammals.',
          conclusion_ar: 'الحوت الأزرق ليس من ذوات الدم البارد.',
          conclusion_en: 'The blue whale is not a cold-blooded animal.',
          distractors_ar: [
            'جميع ذوات الدم البارد تعيش في المحيط.',
            'الحوت الأزرق من الزواحف.',
            'بعض الثدييات فقط دافئة الدم.'
          ],
          distractors_en: [
            'All cold-blooded animals live in oceans.',
            'The blue whale is a reptile.',
            'Only some mammals are warm-blooded.'
          ],
          exp_ar: 'سلب كلي: نفي صفة الدم البارد عن كل الثدييات ينفيها بالضرورة عن الحوت.',
          exp_en: 'Universal negative syllogism: denying cold-bloodedness to all mammals denies it to the whale.'
        },
        {
          premise1_ar: 'كل مضلع منتظم زواياه متساوية وأضلاعه متطابقة.',
          premise1_en: 'Every regular polygon has equal angles and congruent sides.',
          premise2_ar: 'المربع مضلع منتظم.',
          premise2_en: 'A square is a regular polygon.',
          conclusion_ar: 'المربع زواياه متساوية وأضلاعه متطابقة.',
          conclusion_en: 'A square has equal angles and congruent sides.',
          distractors_ar: [
            'كل متساوي الأضلاع هو مربع بالضرورة.',
            'المعين مضلع منتظم دائماً.',
            'المربع له ثلاث زوايا فقط.'
          ],
          distractors_en: [
            'Every equilateral shape is necessarily a square.',
            'A rhombus is always a regular polygon.',
            'A square has only three angles.'
          ],
          exp_ar: 'تطبيق مباشر لتعريف الكيان المنطقي العام على أحد أفراده.',
          exp_en: 'Direct instantiation of a universal geometric definition to a member.'
        },
        {
          premise1_ar: 'كل عدد يقبل القسمة على 6 يقبل القسمة حتمًا على 2 و 3.',
          premise1_en: 'Every integer divisible by 6 is necessarily divisible by 2 and 3.',
          premise2_ar: 'العدد (س) يقبل القسمة على 6.',
          premise2_en: 'Integer X is divisible by 6.',
          conclusion_ar: 'العدد (س) يقبل القسمة على 2 و 3.',
          conclusion_en: 'Integer X is divisible by 2 and 3.',
          distractors_ar: [
            'العدد (س) يقبل القسمة على 12 بالضرورة.',
            'العدد (س) عدد فردي.',
            'كل الأعداد الزوجية تقبل القسمة على 6.'
          ],
          distractors_en: [
            'Integer X is necessarily divisible by 12.',
            'Integer X is an odd number.',
            'All even numbers are divisible by 6.'
          ],
          exp_ar: 'خاصية التحليل إلى العوامل الأولية: القسمة على 6 تقتضي القسمة على 2 و3 منطقياً.',
          exp_en: 'Prime factor entailment: divisibility by 6 strictly entails divisibility by 2 and 3.'
        },
        {
          premise1_ar: 'جميع مصادر الطاقة المتجددة لا تنضب بالاستهلاك البشري العادي.',
          premise1_en: 'All renewable energy sources do not deplete through standard consumption.',
          premise2_ar: 'طاقة الرياح مصدر طاقة متجدد.',
          premise2_en: 'Wind energy is a renewable energy source.',
          conclusion_ar: 'طاقة الرياح لا تنضب بالاستهلاك البشري العادي.',
          conclusion_en: 'Wind energy does not deplete through standard consumption.',
          distractors_ar: [
            'طاقة الرياح تنتج نفايات كربونية.',
            'كل مصادر الطاقة لا تنضب.',
            'طاقة الرياح تعتمد على الوقود الأحفوري.'
          ],
          distractors_en: [
            'Wind energy produces carbon emissions.',
            'All energy sources are inexhaustible.',
            'Wind energy relies on fossil fuels.'
          ],
          exp_ar: 'حكم كلي موجب ينسحب على كل فرد ينضوي تحت الفئة.',
          exp_en: 'Universal affirmative premise strictly binding every categorized member.'
        },
        {
          premise1_ar: 'لا يمكن لجسم ذي كتلة أن يتحرك بسرعة تتجاوز سرعة الضوء في الفراغ.',
          premise1_en: 'No body possessing mass can exceed the vacuum speed of light.',
          premise2_ar: 'المركبة الفضائية تمتلك كتلة فيزيائية.',
          premise2_en: 'The spacecraft possesses physical mass.',
          conclusion_ar: 'المركبة الفضائية لا يمكنها تجاوز سرعة الضوء في الفراغ.',
          conclusion_en: 'The spacecraft cannot exceed the vacuum speed of light.',
          distractors_ar: [
            'المركبة الفضائية تسير بسرعة الضوء دائمًا.',
            'سرعة الضوء تتغير بتغير كتلة المركبة.',
            'المركبة لا كتلة لها في الفضاء.'
          ],
          distractors_en: [
            'The spacecraft travels at light speed at all times.',
            'The speed of light varies with spacecraft mass.',
            'The spacecraft has zero mass in space.'
          ],
          exp_ar: 'قيد كلي نافٍ يمنع الفئة المستهدفة من تجاوز الحد الفيزيائي المحدد.',
          exp_en: 'Universal boundary constraint strictly excluding the mass-bearing subject.'
        },
        {
          premise1_ar: 'كل مثلث قائم الزاوية يحتوي على زاوية واحدة قياسها 90 درجة بالضبط.',
          premise1_en: 'Every right-angled triangle contains exactly one 90-degree angle.',
          premise2_ar: 'المثلث (س ص ع) مثلث قائم الزاوية.',
          premise2_en: 'Triangle XYZ is a right-angled triangle.',
          conclusion_ar: 'المثلث (س ص ع) يحتوي على زاوية واحدة قياسها 90 درجة بالضبط.',
          conclusion_en: 'Triangle XYZ contains exactly one 90-degree angle.',
          distractors_ar: [
            'المثلث يحتوي على زاويتين قائمتين.',
            'كل زوايا المثلث متطابقة وقياسها 60 درجة.',
            'المثلث لا يحتوي على وتر مقابل.'
          ],
          distractors_en: [
            'The triangle contains two right angles.',
            'All angles of the triangle are congruent at 60 degrees.',
            'The triangle has no hypotenuse.'
          ],
          exp_ar: 'الخاصية الجوهرية المعرفة للمثلث القائم تنطبق حتمًا على المثلث المحدد.',
          exp_en: 'Essential definitional property of right triangles applied directly.'
        },
        {
          premise1_ar: 'إذا كان الشكل متوازي أضلاع، فإن كل ضلعين متقابلين فيه متوازيان ومتطابقان.',
          premise1_en: 'If a polygon is a parallelogram, opposite sides are parallel and congruent.',
          premise2_ar: 'المستطيل يمثل حالة خاصة من متوازي الأضلاع.',
          premise2_en: 'A rectangle represents a specific case of a parallelogram.',
          conclusion_ar: 'كل ضلعين متقابلين في المستطيل متوازيان ومتطابقان.',
          conclusion_en: 'Opposite sides in a rectangle are parallel and congruent.',
          distractors_ar: [
            'جميع أضلاع المستطيل متساوية مع القطرين.',
            'المستطيل ليس له أضلاع متوازية.',
            'زوايا متوازي الأضلاع دائمًا 45 درجة.'
          ],
          distractors_en: [
            'All rectangle sides equal the diagonals.',
            'A rectangle has no parallel sides.',
            'Parallelogram angles are always 45 degrees.'
          ],
          exp_ar: 'توارث الخصائص الهندسية: الأشكال الجزئية ترث خصائص الشكل الأم بالضرورة.',
          exp_en: 'Inheritance of geometric invariants from parent class to sub-class.'
        },
        {
          premise1_ar: 'كل مركب عضوي يحتوي في تركيبه الكيميائي على ذرات كربون.',
          premise1_en: 'Every organic chemical compound contains carbon atoms in its structure.',
          premise2_ar: 'الجلوكوز مركب عضوي.',
          premise2_en: 'Glucose is an organic chemical compound.',
          conclusion_ar: 'الجلوكوز يحتوي على ذرات كربون.',
          conclusion_en: 'Glucose contains carbon atoms.',
          distractors_ar: [
            'الجلوكوز لا يحتوي إلا على الكربون فقط.',
            'كل مركب يحتوي على كربون هو جلوكوز.',
            'الجلوكوز مركب غير كيميائي.'
          ],
          distractors_en: [
            'Glucose contains only carbon atoms.',
            'Every compound containing carbon is glucose.',
            'Glucose is not a chemical compound.'
          ],
          exp_ar: 'احتواء الفئة: استلزام وجود الكربون في جميع المركبات العضوية يقتضي وجوده في الجلوكوز.',
          exp_en: 'Class containment: universal presence of carbon strictly entails presence in glucose.'
        },
        {
          premise1_ar: 'جميع الأعداد الأولية الأكبر من 2 هي أعداد فردية.',
          premise1_en: 'All prime numbers greater than 2 are odd numbers.',
          premise2_ar: 'العدد (ص) عدد أولي أكبر من 2.',
          premise2_en: 'Number Y is a prime number greater than 2.',
          conclusion_ar: 'العدد (ص) عدد فردي.',
          conclusion_en: 'Number Y is an odd number.',
          distractors_ar: [
            'العدد (ص) يقبل القسمة على 4.',
            'العدد (ص) عدد زوجي.',
            'العدد 2 عدد فردي.'
          ],
          distractors_en: [
            'Number Y is divisible by 4.',
            'Number Y is an even number.',
            'The number 2 is an odd number.'
          ],
          exp_ar: 'استنتاج قطعي: العدد الأولي الأكبر من 2 لا يمكن أن يكون زوجيًا.',
          exp_en: 'Direct deductive certainty: any prime > 2 is categorically odd.'
        },
        {
          premise1_ar: 'الموجات الصوتية تحتاج حتمًا إلى وسط مادي لتنتقل وتنتشر.',
          premise1_en: 'Sound waves strictly require a material medium to propagate.',
          premise2_ar: 'الفضاء الخارجي فراغ خالٍ من أي وسط مادي.',
          premise2_en: 'Outer space is a vacuum devoid of any material medium.',
          conclusion_ar: 'الموجات الصوتية لا يمكنها الانتشار في الفضاء الخارجي.',
          conclusion_en: 'Sound waves cannot propagate across outer space.',
          distractors_ar: [
            'الصوت ينتشر في الفضاء بسرعة الضوء.',
            'كل الموجات لا تحتاج إلى وسط مادي.',
            'الفضاء مليء بالهواء الكثيف.'
          ],
          distractors_en: [
            'Sound travels across space at the speed of light.',
            'All waves propagate without material media.',
            'Space is filled with dense air.'
          ],
          exp_ar: 'انتفاء الشرط الضروري (الوسط المادي) يؤدي حتمًا إلى انتفاء المشروط (انتشار الصوت).',
          exp_en: 'Absence of necessary condition (medium) strictly prevents the phenomenon (sound propagation).'
        },
        {
          premise1_ar: 'لا يمكن لآلة خالية تمامًا من أي مصدر للطاقة أن تبذل شغلاً ميكانيكيًا.',
          premise1_en: 'No machine completely devoid of an energy source can perform mechanical work.',
          premise2_ar: 'الجهاز (ك) لا يمتلك أي مصدر للطاقة نهائيًا.',
          premise2_en: 'Device K possesses zero energy source.',
          conclusion_ar: 'الجهاز (ك) لا يمكنه بذل أي شغل ميكانيكي.',
          conclusion_en: 'Device K cannot perform any mechanical work.',
          distractors_ar: [
            'الجهاز (ك) يعمل بالطاقة الشمسية.',
            'الجهاز (ك) يبذل شغلاً دائمًا بلا توقف.',
            'كل الأجهزة تبذل شغلاً دون طاقة.'
          ],
          distractors_en: [
            'Device K runs on solar power.',
            'Device K continuously outputs mechanical work.',
            'All machines output work without energy.'
          ],
          exp_ar: 'قانون حفظ الطاقة منطقيًا: غياب الطاقة ينفي إمكانية إنتاج الشغل.',
          exp_en: 'Logical conservation constraint: absence of energy input precludes mechanical output.'
        },
        {
          premise1_ar: 'المحاليل الحمضية النقية تمتلك رقم هيدروجيني (pH) يقل عن 7.',
          premise1_en: 'Pure acidic solutions possess a pH value strictly below 7.',
          premise2_ar: 'السائل (ل) محلول حمضي نقي.',
          premise2_en: 'Liquid L is a pure acidic solution.',
          conclusion_ar: 'السائل (ل) يمتلك رقمًا هيدروجينيًا يقل عن 7.',
          conclusion_en: 'Liquid L possesses a pH value strictly below 7.',
          distractors_ar: [
            'السائل (ل) رقمه الهيدروجيني يساوي 14.',
            'السائل (ل) محلول قلوي قاعدي.',
            'كل السوائل رقمها الهيدروجيني أقل من 7.'
          ],
          distractors_en: [
            'Liquid L has a pH equal to 14.',
            'Liquid L is an alkaline base.',
            'All liquids have a pH below 7.'
          ],
          exp_ar: 'صفة كيميائية ملازمة قطعيًا للفئة تنطبق بالضرورة على العينة.',
          exp_en: 'Inherent category property necessarily attributed to the individual member.'
        },
        {
          premise1_ar: 'كل متجه مقداره الرياضي يساوي صفرًا ليس له اتجاه فراغي محدد.',
          premise1_en: 'Every vector with magnitude equal to zero has no defined spatial direction.',
          premise2_ar: 'المتجه (و) مقداره الرياضي يساوي صفرًا.',
          premise2_en: 'Vector W has magnitude equal to zero.',
          conclusion_ar: 'المتجه (و) ليس له اتجاه فراغي محدد.',
          conclusion_en: 'Vector W has no defined spatial direction.',
          distractors_ar: [
            'المتجه (و) يشير دائمًا إلى الشمال.',
            'المتجه (و) مقداره يساوي واحد.',
            'كل المتجهات ليس لها اتجاه.'
          ],
          distractors_en: [
            'Vector W always points north.',
            'Vector W has magnitude equal to one.',
            'All vectors lack direction.'
          ],
          exp_ar: 'استنتاج منطقي رياضي مباشر للمتجه الصفري (Zero Vector).',
          exp_en: 'Strict mathematical definition deduction for zero vectors.'
        },
        {
          premise1_ar: 'أي نظام مغلق لا يتبادل أي مادة أو طاقة مع الوسط المحيط به.',
          premise1_en: 'Any isolated closed system exchanges neither matter nor energy with surroundings.',
          premise2_ar: 'الحاوية (ع) تمثل نظامًا مغلقًا ومعزولاً تمامًا.',
          premise2_en: 'Container C represents a completely isolated closed system.',
          conclusion_ar: 'الحاوية (ع) لا تتبادل أي مادة أو طاقة مع الوسط المحيط بها.',
          conclusion_en: 'Container C exchanges neither matter nor energy with its surroundings.',
          distractors_ar: [
            'الحاوية (ع) تسرب الحرارة باستمرار.',
            'الحاوية (ع) تمتص المادة من الخارج.',
            'جميع الأنظمة تتبادل المادة دائمًا.'
          ],
          distractors_en: [
            'Container C continuously leaks heat.',
            'Container C absorbs matter from outside.',
            'All physical systems exchange matter.'
          ],
          exp_ar: 'حكم كلي قطعي بالانطباق التام للتعريف المنطقي على النظام المعزول.',
          exp_en: 'Strict logical compliance of the isolated system instance to its universal rule.'
        }
      ];

      const item = prng.pick(syllogisms);
      const allChoices = prng.shuffle([
        { ar: item.conclusion_ar, en: item.conclusion_en, isCorrect: true },
        { ar: item.distractors_ar[0], en: item.distractors_en[0], isCorrect: false },
        { ar: item.distractors_ar[1], en: item.distractors_en[1], isCorrect: false },
        { ar: item.distractors_ar[2], en: item.distractors_en[2], isCorrect: false }
      ]);

      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const stimulusHtml_ar = `
        <div class="iq-syllogism-board" role="region" aria-label="مقدمات القياس المنطقي">
          <div class="iq-syllogism-row">
            <span class="iq-premise-badge">المقدمة 1:</span>
            <span class="iq-premise-content">${item.premise1_ar}</span>
          </div>
          <div class="iq-syllogism-row">
            <span class="iq-premise-badge">المقدمة 2:</span>
            <span class="iq-premise-content">${item.premise2_ar}</span>
          </div>
          <div class="iq-syllogism-conclusion-row">
            <span class="iq-conclusion-badge">الاستنتاج الحتمي:</span>
            <span class="iq-conclusion-target">[ ؟ ]</span>
          </div>
        </div>
      `;

      const stimulusHtml_en = `
        <div class="iq-syllogism-board" role="region" aria-label="Logical Syllogism Premises">
          <div class="iq-syllogism-row">
            <span class="iq-premise-badge">Premise 1:</span>
            <span class="iq-premise-content">${item.premise1_en}</span>
          </div>
          <div class="iq-syllogism-row">
            <span class="iq-premise-badge">Premise 2:</span>
            <span class="iq-premise-content">${item.premise2_en}</span>
          </div>
          <div class="iq-syllogism-conclusion-row">
            <span class="iq-conclusion-badge">Deductive Target:</span>
            <span class="iq-conclusion-target">[ ? ]</span>
          </div>
        </div>
      `;

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'إذا اعتبرت المقدمتان صحيحتين تمامًا، ما هي النتيجة التي تلزم عنهما منطقيًا بالضرورة؟',
        questionText_en: 'Assuming both premises are strictly true, which conclusion follows with logical necessity?',
        stimulusHtml: stimulusHtml_ar,
        stimulusHtml_ar,
        stimulusHtml_en,
        options,
        correctIndex,
        explanation_ar: item.exp_ar,
        explanation_en: item.exp_en
      };
    }
  },

  // 3. Categorical Classification (Odd Word Out / التمييز والتصنيف المنطقي)
  {
    id: 'verbal_categorical_odd_one',
    domain: 'verbal_reasoning',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 30,
    generate(prng, difficulty = 2) {
      const sets = [
        {
          common_ar: ['العين', 'الأذن', 'الأنف'],
          common_en: ['Eye', 'Ear', 'Nose'],
          odd_ar: 'القلب',
          odd_en: 'Heart',
          reason_ar: 'العين والأذن والأنف حواس خارجية تستقبل منبهات المحيط، بينما القلب عضو داخلي دوري.',
          reason_en: 'Eye, Ear, and Nose are external sensory organs; Heart is an internal circulatory organ.'
        },
        {
          common_ar: ['المثلث', 'المربع', 'الخماسي'],
          common_en: ['Triangle', 'Square', 'Pentagon'],
          odd_ar: 'الأسطوانة',
          odd_en: 'Cylinder',
          reason_ar: 'المثلث والمربع والخماسي مضلعات ثنائية الأبعاد، بينما الأسطوانة مجسم هندسي ثلاثي الأبعاد.',
          reason_en: 'Triangle, Square, and Pentagon are 2D polygons; Cylinder is a 3D geometric solid.'
        },
        {
          common_ar: ['الهيليوم', 'النيون', 'الأرجون'],
          common_en: ['Helium', 'Neon', 'Argon'],
          odd_ar: 'الحديد',
          odd_en: 'Iron',
          reason_ar: 'الهيليوم والنيون والأرجون غازات نبيلة خاملة، بينما الحديد فلز معدني صلب وموصل.',
          reason_en: 'Helium, Neon, and Argon are inert noble gases; Iron is a solid metallic conductor.'
        },
        {
          common_ar: ['العدد 11', 'العدد 13', 'العدد 17'],
          common_en: ['Number 11', 'Number 13', 'Number 17'],
          odd_ar: 'العدد 15',
          odd_en: 'Number 15',
          reason_ar: 'الأعداد 11 و 13 و 17 أعداد أولية لا تقبل القسمة إلا على نفسها و1، بينما 15 عدد غير أولي يقبل القسمة على 3 و 5.',
          reason_en: '11, 13, and 17 are prime numbers; 15 is composite (divisible by 3 and 5).'
        },
        {
          common_ar: ['المربع', 'المستطيل', 'شبه المنحرف'],
          common_en: ['Square', 'Rectangle', 'Trapezoid'],
          odd_ar: 'المثلث',
          odd_en: 'Triangle',
          reason_ar: 'المربع والمستطيل وشبه المنحرف أشكال رباعية الأضلاع (4 أضلاع)، بينما المثلث شكل ثلاثي الأضلاع.',
          reason_en: 'Square, Rectangle, and Trapezoid are quadrilaterals (4 sides); Triangle has 3 sides.'
        },
        {
          common_ar: ['السرعة المتجهة', 'القوة', 'التسارع'],
          common_en: ['Velocity', 'Force', 'Acceleration'],
          odd_ar: 'الكتلة',
          odd_en: 'Mass',
          reason_ar: 'السرعة المتجهة والقوة والتسارع كميات فيزيائية متجهة (لها مقدار واتجاه)، بينما الكتلة كمية قياسية (لها مقدار فقط).',
          reason_en: 'Velocity, Force, and Acceleration are vector quantities (magnitude & direction); Mass is scalar.'
        },
        {
          common_ar: ['قنديل البحر', 'الأخطبوط', 'نجم البحر'],
          common_en: ['Jellyfish', 'Octopus', 'Starfish'],
          odd_ar: 'سمك السلمون',
          odd_en: 'Salmon',
          reason_ar: 'قنديل البحر والأخطبوط ونجم البحر كائنات لافقارية (بلا عمود فقري)، بينما السلمون كائن فقاري عظمي.',
          reason_en: 'Jellyfish, Octopus, and Starfish are invertebrates; Salmon is a vertebrate.'
        },
        {
          common_ar: ['الثانية', 'الدقيقة', 'الساعة'],
          common_en: ['Second', 'Minute', 'Hour'],
          odd_ar: 'المتر',
          odd_en: 'Meter',
          reason_ar: 'الثانية والدقيقة والساعة وحدات قياس للزمن، بينما المتر وحدة قياس للمسافة والأطوال.',
          reason_en: 'Second, Minute, and Hour measure time duration; Meter measures spatial distance.'
        },
        {
          common_ar: ['الفحم الحجري', 'النفط الخام', 'الغاز الطبيعي'],
          common_en: ['Coal', 'Crude Oil', 'Natural Gas'],
          odd_ar: 'الإشعاع الشمسي',
          odd_en: 'Solar Radiation',
          reason_ar: 'الفحم والنفط والغاز وقود أحفوري ناضب وغير متجدد، بينما الإشعاع الشمسي طاقة متجددة مستمرة.',
          reason_en: 'Coal, Oil, and Gas are depletable fossil fuels; Solar radiation is renewable.'
        },
        {
          common_ar: ['الشمال', 'الجنوب', 'الشرق'],
          common_en: ['North', 'South', 'East'],
          odd_ar: 'الارتفاع الرأسي',
          odd_en: 'Altitude',
          reason_ar: 'الشمال والجنوب والشرق اتجاهات أفقية جغرافية ثنائية الأبعاد، بينما الارتفاع يمثل البعد الرأسي الثالث.',
          reason_en: 'North, South, and East are 2D planar cardinal bearings; Altitude is the vertical dimension.'
        },
        {
          common_ar: ['ذاكرة RAM', 'ذاكرة Cache', 'سجلات المعالج Registers'],
          common_en: ['RAM Memory', 'Cache Memory', 'CPU Registers'],
          odd_ar: 'القرص الصلب Hard Drive',
          odd_en: 'Hard Drive Storage',
          reason_ar: 'ذاكرة RAM وCache وRegisters ذواكر سريعة متطايرة تفقد بياناتها بانقطاع الطاقة، بينما القرص الصلب وسيط تخزين دائم غير متطاير.',
          reason_en: 'RAM, Cache, and Registers are volatile primary memories; Hard Drive is non-volatile secondary storage.'
        },
        {
          common_ar: ['المجهر', 'التلسكوب', 'المنظار البصري'],
          common_en: ['Microscope', 'Telescope', 'Binoculars'],
          odd_ar: 'السماعة الطبية',
          odd_en: 'Stethoscope',
          reason_ar: 'المجهر والتلسكوب والمنظار أدوات بصرية تعتمد على الضوء والعدسات، بينما السماعة أداة صوتية ترصد الاهتزازات السمعية.',
          reason_en: 'Microscope, Telescope, and Binoculars are optical instruments; Stethoscope is an acoustic instrument.'
        },
        {
          common_ar: ['الملي', 'الميكرو', 'النانو'],
          common_en: ['Milli', 'Micro', 'Nano'],
          odd_ar: 'الكيلو',
          odd_en: 'Kilo',
          reason_ar: 'الملي والميكرو والنانو بادئات قياسية لأجزاء أصغر من الواحد، بينما الكيلو بادئة مضاعفة أكبر من الواحد (1000).',
          reason_en: 'Milli, Micro, and Nano denote sub-unit fractions (<1); Kilo denotes a multiple (1000).'
        },
        {
          common_ar: ['رباعي الأوجه Tetrahedron', 'المكعب Cube', 'ثماني الأوجه Octahedron'],
          common_en: ['Tetrahedron', 'Cube', 'Octahedron'],
          odd_ar: 'المخروط Cone',
          odd_en: 'Cone',
          reason_ar: 'رباعي الأوجه والمكعب وثماني الأوجه مجسمات أفلاطونية منتظمة ذات وجوه مستوية متطابقة، بينما المخروط مجسم ذو سطح منحنٍ وقاعدة دائرية.',
          reason_en: 'Tetrahedron, Cube, and Octahedron are regular polyhedra with flat faces; Cone has a curved surface.'
        },
        {
          common_ar: ['البروتون', 'النيوترون', 'الكوارك'],
          common_en: ['Proton', 'Neutron', 'Quark'],
          odd_ar: 'الإلكترون',
          odd_en: 'Electron',
          reason_ar: 'البروتون والنيوترون والكوارك هادرونات جسيمية ترتبط بالنواة الذرية عبر القوة النووية الشديدة، بينما الإلكترون لبتون خارج النواة.',
          reason_en: 'Proton, Neutron, and Quark are nuclear hadrons governed by strong force; Electron is an extranuclear lepton.'
        },
        {
          common_ar: ['الجمع', 'الضرب', 'الرفع للأس'],
          common_en: ['Addition', 'Multiplication', 'Exponentiation'],
          odd_ar: 'علامة التساوي (=)',
          odd_en: 'Equals sign (=)',
          reason_ar: 'الجمع والضرب والأس عمليات حسابية تحويلية تُجري تغييراً على القيم، بينما التساوي علاقة مقارنة منطقية تكافئية.',
          reason_en: 'Addition, Multiplication, and Exponentiation are generative operators; Equals is an equivalence relation.'
        },
        {
          common_ar: ['الماس', 'الجرافيت', 'الفوليرين'],
          common_en: ['Diamond', 'Graphite', 'Fullerene'],
          odd_ar: 'الذهب',
          odd_en: 'Gold',
          reason_ar: 'الماس والجرافيت والفوليرين صور تآصلية متعددة لعنصر واحد هو الكربون، بينما الذهب عنصر فلزي مستقل بذاته.',
          reason_en: 'Diamond, Graphite, and Fullerene are allotropes of pure Carbon; Gold is a distinct chemical element.'
        },
        {
          common_ar: ['الدائرة', 'القطع الناقص', 'المقطع المكافئ'],
          common_en: ['Circle', 'Ellipse', 'Parabola'],
          odd_ar: 'المكعب',
          odd_en: 'Cube',
          reason_ar: 'الدائرة والقطع الناقص والمكافئ قطوع مخروطية ثنائية الأبعاد، بينما المكعب مجسم فراغي ثلاثي الأبعاد.',
          reason_en: 'Circle, Ellipse, and Parabola are 2D conic sections; Cube is a 3D solid.'
        }
      ];

      const item = prng.pick(sets);
      const allChoices = prng.shuffle([
        { ar: item.odd_ar, en: item.odd_en, isCorrect: true },
        { ar: item.common_ar[0], en: item.common_en[0], isCorrect: false },
        { ar: item.common_ar[1], en: item.common_en[1], isCorrect: false },
        { ar: item.common_ar[2], en: item.common_en[2], isCorrect: false }
      ]);

      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: 'حدد الكلمة الشاذة أو المختلفة التي لا تشترك في الخاصية الجوهرية مع باقي الكلمات الثلاث:',
        questionText_en: 'Identify the odd item out that does not share the fundamental structural property of the other three:',
        stimulusHtml: '',
        options,
        correctIndex,
        explanation_ar: item.reason_ar,
        explanation_en: item.reason_en
      };
    }
  },

  // 4. Intensity / Gradation Spectrum (تدرج الشدة والقياس الدلالي)
  {
    id: 'verbal_antonym_graded_spectrum',
    domain: 'verbal_reasoning',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 30,
    generate(prng, difficulty = 2) {
      const spectrums = [
        {
          pair1_ar: ['همس', 'صراخ'],
          pair1_en: ['Whisper', 'Shout'],
          itemC_ar: 'نسيم',
          itemC_en: 'Breeze',
          correct_ar: 'عاصفة عاتية',
          correct_en: 'Gale / Tempest',
          distractors_ar: ['هواء خفيف', 'غيم', 'مطر'],
          distractors_en: ['Mild air', 'Cloud', 'Rain'],
          exp_ar: 'تدرج شدة الظاهرة من الحد الأدنى للحد الأقصى: همس (أقل صوت) إلى صراخ (أعلى صوت)، يقابله نسيم (أهدأ رياح) إلى عاصفة.',
          exp_en: 'Progression from minimum to maximum intensity: quietest sound to loudest sound corresponds to mildest wind to strongest gale.'
        },
        {
          pair1_ar: ['دافئ', 'حارق'],
          pair1_en: ['Warm', 'Scorching'],
          itemC_ar: 'بارد',
          itemC_en: 'Cool',
          correct_ar: 'متجمد صقيعي',
          correct_en: 'Freezing / Frigid',
          distractors_ar: ['معتدل', 'رطب', 'سائل'],
          distractors_en: ['Mild', 'Damp', 'Liquid'],
          exp_ar: 'التدرج من الدرجة المعتدلة إلى الدرجة القصوى المتطرفة لنفس المحور الحراري.',
          exp_en: 'Intensification from moderate intensity to peak extreme along the identical thermal axis.'
        },
        {
          pair1_ar: ['وميض خافت', 'سطوع باهر'],
          pair1_en: ['Glimmer', 'Blinding Glare'],
          itemC_ar: 'رذاذ ماء',
          itemC_en: 'Drizzle',
          correct_ar: 'سيل جارف',
          correct_en: 'Torrent / Downpour',
          distractors_ar: ['ندى صباحي', 'كوب ماء', 'بحر'],
          distractors_en: ['Morning dew', 'Cup of water', 'Sea'],
          exp_ar: 'الانتقال من أدنى مستويات الظاهرة التدفقية إلى أقصاها عنفًا وغزارة.',
          exp_en: 'Progression from the lowest baseline manifestation to the maximum violent volume.'
        },
        {
          pair1_ar: ['قضم طفيف', 'التهام كلي'],
          pair1_en: ['Nibble', 'Devour'],
          itemC_ar: 'نقر لطيف',
          itemC_en: 'Tap',
          correct_ar: 'ضربة قاصمة',
          correct_en: 'Pound / Sledge strike',
          distractors_ar: ['لمسة هادئة', 'إشارة', 'صوت'],
          distractors_en: ['Gentle touch', 'Signal', 'Sound'],
          exp_ar: 'تدرج القوة الميكانيكية المبذولة من الحد الأدنى الحذر إلى الحد الأقصى التدميري.',
          exp_en: 'Kinetic intensity escalation from minimal delicate action to maximal forceful impact.'
        },
        {
          pair1_ar: ['انزعاج عابر', 'غضب عارم'],
          pair1_en: ['Annoyed', 'Enraged'],
          itemC_ar: 'رضا هادئ',
          itemC_en: 'Content',
          correct_ar: 'ابتهاج غامر',
          correct_en: 'Ecstatic / Euphoric',
          distractors_ar: ['هدوء روتيني', 'ابتسامة', 'قبول'],
          distractors_en: ['Routine calm', 'Smile', 'Acceptance'],
          exp_ar: 'التدرج في الشدة الشعورية من المستوى الأولي العادي إلى ذروة الانفعال.',
          exp_en: 'Emotional gradation from baseline reaction to its maximum psychological peak.'
        },
        {
          pair1_ar: ['بركة ماء', 'محيط'],
          pair1_en: ['Puddle', 'Ocean'],
          itemC_ar: 'تلة ترابية',
          itemC_en: 'Hillock',
          correct_ar: 'قمة جبلية شاهقة',
          correct_en: 'Mountain Peak',
          distractors_ar: ['حفرة', 'صخرة', 'وادي'],
          distractors_en: ['Pothole', 'Stone', 'Valley'],
          rel_ar: 'التدرج في الحجم الجغرافي من أدنى مقياس مكاني إلى أعلى تشكيل تضاريسي.',
          rel_en: 'Spatial scale gradation from miniature accumulation to colossal geological formation.'
        },
        {
          pair1_ar: ['شرارة', 'حريق مروع'],
          pair1_en: ['Spark', 'Conflagration'],
          itemC_ar: 'تموج طفيف',
          itemC_en: 'Ripple',
          correct_ar: 'موجة تسونامي عاتية',
          correct_en: 'Tsunami Wave',
          distractors_ar: ['قطرة ماء', 'رياح', 'شاطئ'],
          distractors_en: ['Water drop', 'Wind', 'Shore'],
          exp_ar: 'تطور الاضطراب الأولي الدقيق إلى ظاهرة كارثية كبرى ناتجة عن تضخمه.',
          exp_en: 'Initial minute disturbance scaled to catastrophic systemic manifestation.'
        },
        {
          pair1_ar: ['لمحة سريعة', 'فحص دقيق مفصل'],
          pair1_en: ['Glance', 'Scrutiny'],
          itemC_ar: 'قراءة عابرة',
          itemC_en: 'Skim',
          correct_ar: 'دراسة استقصائية متعمقة',
          correct_en: 'Exhaustive Analysis',
          distractors_ar: ['تصفح كتاب', 'ورقة', 'ملاحظة'],
          distractors_en: ['Page flip', 'Paper', 'Note'],
          exp_ar: 'تدرج الجهد الإدراكي من الملاحظة العابرة إلى أقصى درجات التركيز التحليلي.',
          exp_en: 'Cognitive effort gradation from superficial exposure to deep structural audit.'
        },
        {
          pair1_ar: ['خرير ماء', 'هدير شلال'],
          pair1_en: ['Trickle', 'Roaring Cascade'],
          itemC_ar: 'دندنة',
          itemC_en: 'Hum',
          correct_ar: 'صخب مدوٍ',
          correct_en: 'Deafening Clamor',
          distractors_ar: ['كلمة', 'لحن', 'أذن'],
          distractors_en: ['Word', 'Tune', 'Ear'],
          exp_ar: 'تدرج الشدة الصوتية والاهتزازية من أدنى المستويات إلى أعلى سعة موجية.',
          exp_en: 'Acoustic amplitude escalation from soft murmur to overwhelming volume.'
        },
        {
          pair1_ar: ['تردد طفيف', 'ذعر مشل للحركة'],
          pair1_en: ['Hesitation', 'Paralyzing Panic'],
          itemC_ar: 'حذر عقلاني',
          itemC_en: 'Prudence',
          correct_ar: 'ارتياب قهري مفرط',
          correct_en: 'Extreme Paranoia',
          distractors_ar: ['تفكير', 'أمان', 'توقف'],
          distractors_en: ['Thinking', 'Safety', 'Pause'],
          exp_ar: 'الانتقال من رد الفعل الوقائي الطبيعي إلى شكله المرضي المتطرف الحاد.',
          exp_en: 'Escalation of self-preservation response into an extreme incapacitating pathology.'
        },
        {
          pair1_ar: ['وخزة إبرة', 'طعنة نافذة'],
          pair1_en: ['Pinprick', 'Deep Puncture'],
          itemC_ar: 'خدش سطحي',
          itemC_en: 'Scratch',
          correct_ar: 'تمزق غائر',
          correct_en: 'Severe Laceration',
          distractors_ar: ['ألم طفيف', 'ضمادة', 'جلد'],
          distractors_en: ['Mild ache', 'Bandage', 'Skin'],
          exp_ar: 'تدرج شدة التأثير الفيزيائي من السطحي المحدود إلى العميق المؤثر.',
          exp_en: 'Depth and severity escalation from superficial mark to structural compromise.'
        },
        {
          pair1_ar: ['حرص مالي', 'بخل مدقع'],
          pair1_en: ['Frugality', 'Extreme Miserliness'],
          itemC_ar: 'كرم وسخاء',
          itemC_en: 'Generosity',
          correct_ar: 'تبذير وسفه',
          correct_en: 'Lavish Prodigality',
          distractors_ar: ['مال', 'غنى', 'إنفاق'],
          distractors_en: ['Money', 'Wealth', 'Spending'],
          exp_ar: 'تجاوز الفضيلة المتوازنة إلى طرف الإفراط غير الرشيد المقابل.',
          exp_en: 'Transformation of a balanced virtue into its destructive unconstrained extreme.'
        },
        {
          pair1_ar: ['ميل طفيف', 'هاوية عمودية سحيقة'],
          pair1_en: ['Gentle Slope', 'Vertical Precipice'],
          itemC_ar: 'تباطؤ سير',
          itemC_en: 'Slowing down',
          correct_ar: 'توقف تام فجائي',
          correct_en: 'Abrupt Dead Stop',
          distractors_ar: ['خطوة', 'طريق', 'حركة'],
          distractors_en: ['Step', 'Road', 'Motion'],
          exp_ar: 'تدرج الانحدار الحركي أو الهندسي من التغير الطفيف إلى الحد الحاسم النهائي.',
          exp_en: 'Gradation from mild gradient variation to complete absolute threshold termination.'
        },
        {
          pair1_ar: ['رائحة عابرة', 'نتانة خانقة'],
          pair1_en: ['Faint Whiff', 'Suffocating Stench'],
          itemC_ar: 'طعم لاذع قليلاً',
          itemC_en: 'Mild Tang',
          correct_ar: 'مرارة حارقة لا تُطاق',
          correct_en: 'Unbearable Acrid Bitterness',
          distractors_ar: ['طعام', 'لسان', 'نكهة'],
          distractors_en: ['Food', 'Tongue', 'Flavor'],
          exp_ar: 'تدرج التركيز الحسي الكيميائي من العتبة الأولية للإدراك إلى أقصى إشباع منفر.',
          exp_en: 'Chemosensory concentration scaling from detection threshold to overwhelming saturation.'
        }
      ];

      const item = prng.pick(spectrums);
      const allChoices = prng.shuffle([
        { ar: item.correct_ar, en: item.correct_en, isCorrect: true },
        { ar: item.distractors_ar[0], en: item.distractors_en[0], isCorrect: false },
        { ar: item.distractors_ar[1], en: item.distractors_en[1], isCorrect: false },
        { ar: item.distractors_ar[2], en: item.distractors_en[2], isCorrect: false }
      ]);

      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا كانت العلاقة بين (${item.pair1_ar[0]}) و (${item.pair1_ar[1]}) تعبر عن تدرج الشدة من الأدنى إلى الأقصى، فما الذي يقابل (${item.itemC_ar}) بنفس المنطق؟`,
        questionText_en: `If (${item.pair1_en[0]}) to (${item.pair1_en[1]}) represents escalation from minimal to maximal intensity, what matches (${item.itemC_en}) by the same logic?`,
        stimulusHtml: '',
        options,
        correctIndex,
        explanation_ar: item.exp_ar,
        explanation_en: item.exp_en
      };
    }
  },

  // 5. Conditional Logic Inference (قواعد الاستلزام ونفي التالي Modus Tollens)
  {
    id: 'verbal_conditional_inference',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const scenarios = [
        {
          rule_ar: 'إذا أمطرت السماء بغزارة، فإن أرضية الملعب تبتل حتمًا.',
          rule_en: 'If it rains heavily, the sports field is inevitably wet.',
          fact_ar: 'الملاحظة المؤكدة: أرضية الملعب جافة تمامًا وغير مبللة.',
          fact_en: 'Confirmed observation: The sports field is completely dry.',
          correct_ar: 'السماء لم تمطر بغزارة.',
          correct_en: 'It did not rain heavily.',
          distractors_ar: [
            'السماء أوشكت على المطر.',
            'أرضية الملعب تم تجفيفها بعد المطر.',
            'قد تكون أمطرت في مكان آخر بعيد.'
          ],
          distractors_en: [
            'It is about to rain.',
            'The field was artificially dried after rain.',
            'It might have rained elsewhere.'
          ],
          exp_ar: 'قاعدة نفي التالي (Modus Tollens): إذا كانت (أ) تستلزم (ب)، وثبت نفي (ب)، فإن (أ) لم تحدث حتمًا.',
          exp_en: 'Modus Tollens: If P implies Q, and Not Q is observed, then Not P must strictly hold.'
        },
        {
          rule_ar: 'إذا اشتعلت النيران في المحرك، فإن حساس الإنذار ينطلق بالصوت فورًا.',
          rule_en: 'If fire ignites inside the engine, the acoustic alarm triggers immediately.',
          fact_ar: 'الملاحظة المؤكدة: حساس الإنذار صامت تمامًا ولم ينطلق.',
          fact_en: 'Confirmed observation: The acoustic alarm remains completely silent.',
          correct_ar: 'لم تشتعل النيران في المحرك.',
          correct_en: 'No fire ignited inside the engine.',
          distractors_ar: [
            'المحرك يعمل بأقصى طاقته.',
            'الحساس بحاجة لصيانة كهربائية.',
            'النيران اشتعلت ثم انطفأت تلقائياً.'
          ],
          distractors_en: [
            'The engine is running at peak output.',
            'The alarm sensor needs maintenance.',
            'Fire ignited and extinguished itself.'
          ],
          exp_ar: 'غياب الأثر الحتمي المشروط ينفي وقوع الشرط الكافي المسبب له.',
          exp_en: 'Absence of the guaranteed consequent refutes the occurrence of the sufficient antecedent.'
        },
        {
          rule_ar: 'إذا كان العدد الصحيح موجبًا وزوجيًا، فإن مربعه يكون عددًا زوجيًا حتمًا.',
          rule_en: 'If an integer is positive and even, its square is necessarily an even integer.',
          fact_ar: 'الملاحظة المؤكدة: مربع العدد الصحيح (ن) هو عدد فردي.',
          fact_en: 'Confirmed observation: The square of integer N is an odd integer.',
          correct_ar: 'العدد الصحيح (ن) ليس عددًا زوجيًا موجبًا.',
          correct_en: 'Integer N is not a positive even number.',
          distractors_ar: [
            'العدد (ن) يساوي صفراً.',
            'مربع العدد (ن) يقبل القسمة على 4.',
            'العدد (ن) غير موجود.'
          ],
          distractors_en: [
            'Integer N equals zero.',
            'The square of N is divisible by 4.',
            'Integer N does not exist.'
          ],
          exp_ar: 'نفي النتيجة الزوجية في المربع يستلزم منطقيًا نفي كون الأصل عددًا زوجيًا.',
          exp_en: 'Negation of the even square strictly entails negation of the even integer hypothesis.'
        },
        {
          rule_ar: 'إذا انقطع التيار الكهربائي عن المعمل، فإن أجهزة التبريد تتوقف عن العمل.',
          rule_en: 'If electricity disconnects from the lab, all refrigeration units halt.',
          fact_ar: 'الملاحظة المؤكدة: أجهزة التبريد في المعمل تعمل بكفاءة مستمرة.',
          fact_en: 'Confirmed observation: Refrigeration units are operating continuously.',
          correct_ar: 'التيار الكهربائي لم ينقطع عن المعمل.',
          correct_en: 'Electricity did not disconnect from the lab.',
          distractors_ar: [
            'الأجهزة تعمل بالبطارية الداخلية.',
            'درجة حرارة المعمل ارتفعت بشدة.',
            'الكهرباء انقطعت وعادت في ثانية.'
          ],
          distractors_en: [
            'Units run on internal battery reserve.',
            'Lab temperature rose drastically.',
            'Power flickered off and on in one second.'
          ],
          exp_ar: 'استمرار عمل الأجهزة ينفي بالضرورة وقوع شرط الانقطاع التام للتيار.',
          exp_en: 'Continuous operation directly contradicts the condition of power disconnection.'
        },
        {
          rule_ar: 'إذا كان المثلث متساوي الأضلاع، فإن جميع زواياه تكون متطابقة وقياس كل منها 60 درجة.',
          rule_en: 'If a triangle is equilateral, all its angles are congruent at 60 degrees each.',
          fact_ar: 'الملاحظة المؤكدة: المثلث (د هـ و) يحتوي على زاوية قياسها 45 درجة.',
          fact_en: 'Confirmed observation: Triangle DEF contains an angle measuring 45 degrees.',
          correct_ar: 'المثلث (د هـ و) ليس متساوي الأضلاع.',
          correct_en: 'Triangle DEF is not equilateral.',
          distractors_ar: [
            'المثلث متساوي الأضلاع لكن مقلوب.',
            'مجموع زوايا المثلث لا يساوي 180.',
            'الضلعان الآخران متطابقان حتماً.'
          ],
          distractors_en: [
            'The triangle is equilateral but inverted.',
            'Triangle angles do not sum to 180.',
            'The remaining two sides are identical.'
          ],
          exp_ar: 'وجود زاوية تخالف 60 درجة ينفي بالضرورة القاطعة صفة تطابق الأضلاع الثلاثة.',
          exp_en: 'An angle violating the 60-degree invariant strictly refutes equilateral classification.'
        },
        {
          rule_ar: 'إذا كان السائل ماءً مقطرًا نقيًا، فإن درجة غليانه تحت الضغط الجوي المعياري هي 100° مئوية.',
          rule_en: 'If a liquid is pure distilled water, its boiling point at 1 atm is exactly 100°C.',
          fact_ar: 'الملاحظة المؤكدة: السائل غلى عند 108° مئوية تحت الضغط الجوي المعياري.',
          fact_en: 'Confirmed observation: The liquid boiled at 108°C under 1 atm pressure.',
          correct_ar: 'السائل ليس ماءً مقطرًا نقيًا.',
          correct_en: 'The liquid is not pure distilled water.',
          distractors_ar: [
            'ميزان الحرارة معطل تمامًا.',
            'السائل تبخر قبل أن يغلي.',
            'الضغط الجوي كان منخفضًا في التجربة.'
          ],
          distractors_en: [
            'The thermometer is completely defective.',
            'Liquid evaporated before boiling.',
            'Atmospheric pressure was low during test.'
          ],
          exp_ar: 'اختلاف درجة الغليان تحت نفس الضغط المعياري يثبت عدم نقاء السائل كيميائياً.',
          exp_en: 'Discrepancy in boiling point at standard pressure strictly refutes pure distilled status.'
        },
        {
          rule_ar: 'إذا حصل الطالب على الدرجة الكاملة في الامتحان، فإنه يحصل على الترتيب الأول في الفصل.',
          rule_en: 'If a student scores 100% on the exam, they are guaranteed first rank in class.',
          fact_ar: 'الملاحظة المؤكدة: الطالب طارق لم يحصل على الترتيب الأول في الفصل.',
          fact_en: 'Confirmed observation: Student Tariq did not achieve first rank in class.',
          correct_ar: 'طارق لم يحصل على الدرجة الكاملة في الامتحان.',
          correct_en: 'Tariq did not score 100% on the exam.',
          distractors_ar: [
            'طارق غاب عن الامتحان بالكامل.',
            'طارق حصل على أدنى درجة في الفصل.',
            'المعلم أخطأ في رصد الدرجات.'
          ],
          distractors_en: [
            'Tariq was absent from the exam.',
            'Tariq received the lowest grade.',
            'The teacher made a clerical scoring error.'
          ],
          exp_ar: 'نفي الترتيب الأول ينفي بالضرورة حصوله على الدرجة الكاملة المستلزمة للمركز الأول.',
          exp_en: 'Failure to secure first rank strictly negates having scored the sufficient 100%.'
        },
        {
          rule_ar: 'إذا كانت بطارية الهاتف مشحونة، فإن شاشته تضيء فور الضغط على زر التشغيل.',
          rule_en: 'If the phone battery has charge, the display illuminates on power press.',
          fact_ar: 'الملاحظة المؤكدة: الشاشة بقيت مظلمة تمامًا عند الضغط على زر التشغيل (مع سلامة الشاشة والزر).',
          fact_en: 'Confirmed observation: Display stayed dark upon press (with screen and button intact).',
          correct_ar: 'بطارية الهاتف ليست مشحونة (فارغة).',
          correct_en: 'The phone battery is not charged (depleted).',
          distractors_ar: [
            'الهاتف متصل بالإنترنت.',
            'الهاتف في وضع الطيران.',
            'الشاشة مكسورة داخليًا.'
          ],
          distractors_en: [
            'Phone is connected to Wi-Fi.',
            'Phone is in airplane mode.',
            'Display is internally cracked.'
          ],
          exp_ar: 'تحت افتراض سلامة الأجزاء، غياب الإضاءة يثبت منطقيًا نفاذ شحنة البطارية.',
          exp_en: 'Under operational integrity of parts, lack of illumination strictly proves battery exhaustion.'
        },
        {
          rule_ar: 'إذا كان الحيوان ثدييًا، فإنه يتنفس بالرئتين في جميع مراحل حياته.',
          rule_en: 'If an animal is a mammal, it respires via lungs throughout all life stages.',
          fact_ar: 'الملاحظة المؤكدة: الكائن الحي (ص) يتنفس بالخياشيم فقط.',
          fact_en: 'Confirmed observation: Organism Y respires solely via gills.',
          correct_ar: 'الكائن الحي (ص) ليس من الثدييات.',
          correct_en: 'Organism Y is not a mammal.',
          distractors_ar: [
            'الكائن (ص) يعيش في قمة الجبل.',
            'الكائن (ص) من الطيور المهاجرة.',
            'كل الكائنات المائية ثدييات.'
          ],
          distractors_en: [
            'Organism Y lives on mountain summits.',
            'Organism Y is a migratory bird.',
            'All aquatic animals are mammals.'
          ],
          exp_ar: 'وجود الخياشيم وغياب الرئتين ينفي منطقيًا انتماء الكائن لطائفة الثدييات.',
          exp_en: 'Gills respiration strictly contradicts the defining pulmonary respiration invariant of mammals.'
        },
        {
          rule_ar: 'إذا كانت السيارة تسير في الاتجاه الصحيح للشارع، فإن كاميرا المراقبة تسجل لوحتها باللون الأخضر.',
          rule_en: 'If the car travels in the lawful direction, the surveillance camera logs green.',
          fact_ar: 'الملاحظة المؤكدة: كاميرا المراقبة سجلت لوحة السيارة باللون الأحمر وليس الأخضر.',
          fact_en: 'Confirmed observation: The surveillance camera logged red instead of green.',
          correct_ar: 'السيارة لم تكن تسير في الاتجاه الصحيح للشارع.',
          correct_en: 'The car was not traveling in the lawful direction.',
          distractors_ar: [
            'السيارة لا تمتلك لوحة أرقام.',
            'كاميرا المراقبة متوقفة عن التسجيل.',
            'السيارة تجاوزت السرعة القانونية.'
          ],
          distractors_en: [
            'The car has no license plates.',
            'The camera stopped recording.',
            'The car exceeded the speed limit.'
          ],
          exp_ar: 'عدم ظهور الإشارة الخضراء ينفي تحقق شرط السير في الاتجاه الصحيح.',
          exp_en: 'Absence of green confirmation strictly refutes adherence to the lawful direction.'
        },
        {
          rule_ar: 'إذا دخل المتسابق ضمن الثلاثة الأوائل، فإنه يصعد إلى منصة التتويج للتكريم.',
          rule_en: 'If a runner finishes in the top 3, they stand on the podium for medals.',
          fact_ar: 'الملاحظة المؤكدة: المتسابق سامي لم يصعد إلى منصة التتويج.',
          fact_en: 'Confirmed observation: Runner Sami did not stand on the podium.',
          correct_ar: 'سامي لم يدخل ضمن المتسابقين الثلاثة الأوائل.',
          correct_en: 'Sami did not finish in the top 3.',
          distractors_ar: [
            'سامي انسحب قبل بدء السباق.',
            'سامي فاز بالمركز الأول.',
            'جميع المتسابقين صعدوا للمنصة.'
          ],
          distractors_en: [
            'Sami withdrew before start.',
            'Sami won 1st place.',
            'All runners stood on podium.'
          ],
          exp_ar: 'عدم الصعود للمنصة ينفي منطقياً كونه من الثلاثة الأوائل.',
          exp_en: 'Absence from podium categorically refutes top 3 finish.'
        },
        {
          rule_ar: 'إذا كانت الأرضية ملساء تمامًا وعديمة الاحتكاك، فإن الجسم المتحرك يستمر في حركته للأبد بسرعة ثابتة.',
          rule_en: 'If a surface is frictionless, a moving body continues indefinitely at constant velocity.',
          fact_ar: 'الملاحظة المؤكدة: الجسم المتحرك تباطأت سرعته تدريجيًا حتى توقف تمامًا.',
          fact_en: 'Confirmed observation: The moving body decelerated gradually to a complete halt.',
          correct_ar: 'الأرضية لم تكن ملساء تمامًا أو خالية من الاحتكاك.',
          correct_en: 'The surface was not frictionless.',
          distractors_ar: [
            'الجسم المتحرك لا كتلة له.',
            'قوانين الفيزياء لا تنطبق على هذا الجسم.',
            'السرعة كانت أعلى من سرعة الصوت.'
          ],
          distractors_en: [
            'The moving body has zero mass.',
            'Physics laws do not apply.',
            'Speed was supersonic.'
          ],
          exp_ar: 'تباطؤ الجسم وتوقفه يثبت منطقيًا وجود قوة مقاومة واحتكاك أثرت عليه.',
          exp_en: 'Deceleration to halt strictly refutes the frictionless premise.'
        },
        {
          rule_ar: 'إذا كان الصندوق يحتوي على ذهب خالص، فإن كتلته تكون أكبر من 10 كيلوغرامات حتمًا.',
          rule_en: 'If the crate contains pure gold, its mass is strictly greater than 10 kg.',
          fact_ar: 'الملاحظة المؤكدة: كتلة الصندوق وُجدت تساوي 4 كيلوغرامات فقط.',
          fact_en: 'Confirmed observation: The crate mass is measured at exactly 4 kg.',
          correct_ar: 'الصندوق لا يحتوي على ذهب خالص.',
          correct_en: 'The crate does not contain pure gold.',
          distractors_ar: [
            'الصندوق فارغ تمامًا.',
            'الميزان معطل ومكسور.',
            'الصندوق يحتوي على ألماس.'
          ],
          distractors_en: [
            'The crate is completely empty.',
            'The balance scale is defective.',
            'The crate contains diamonds.'
          ],
          exp_ar: 'الكتلة الأقل من 10 كجم تنفي بالضرورة القاطعة احتواء الصندوق على الذهب الخالص المحدد.',
          exp_en: 'Mass below the 10kg threshold definitively rules out pure gold content.'
        },
        {
          rule_ar: 'إذا تم قفل البوابة الإلكترونية بالرمز السري، فإن المؤشر الضوئي يتحول إلى اللون الأحمر.',
          rule_en: 'If the gate is locked with the passcode, the light turns solid red.',
          fact_ar: 'الملاحظة المؤكدة: المؤشر الضوئي للبوابة لونه أزرق وليس أحمر.',
          fact_en: 'Confirmed observation: The gate light is blue, not red.',
          correct_ar: 'البوابة لم تُقفل بالرمز السري.',
          correct_en: 'The gate was not locked with the passcode.',
          distractors_ar: [
            'البوابة مفتوحة على مصراعيها.',
            'الرمز السري غير صحيح.',
            'الكهرباء انقطعت عن البوابة.'
          ],
          distractors_en: [
            'The gate is fully opened.',
            'The passcode is wrong.',
            'Power disconnected from gate.'
          ],
          exp_ar: 'نفي اللون الأحمر ينفي تحقق القفل بالرمز السري المرتبط به.',
          exp_en: 'Absence of red light strictly denies the locked state.'
        }
      ];

      const item = prng.pick(scenarios);
      const allChoices = prng.shuffle([
        { ar: item.correct_ar, en: item.correct_en, isCorrect: true },
        { ar: item.distractors_ar[0], en: item.distractors_en[0], isCorrect: false },
        { ar: item.distractors_ar[1], en: item.distractors_en[1], isCorrect: false },
        { ar: item.distractors_ar[2], en: item.distractors_en[2], isCorrect: false }
      ]);

      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `بناءً على القاعدة: "${item.rule_ar}"، والملاحظة المؤكدة: "${item.fact_ar}"، ما النتيجة المنطقية الحتمية؟`,
        questionText_en: `Given the rule: "${item.rule_en}", and confirmed observation: "${item.fact_en}", what is the certain logical conclusion?`,
        stimulusHtml: '',
        options,
        correctIndex,
        explanation_ar: item.exp_ar,
        explanation_en: item.exp_en
      };
    }
  },

  // 6. Hierarchical Sequence Taxonomy (التسلسل الهرمي البنائي)
  {
    id: 'verbal_hierarchical_sequence',
    domain: 'verbal_reasoning',
    supportedDifficulties: [1, 2, 3],
    expectedTimeSeconds: 30,
    generate(prng, difficulty = 2) {
      const hierarchies = [
        {
          seq_ar: ['حرف', 'كلمة', 'جملة'],
          seq_en: ['Letter', 'Word', 'Sentence'],
          correct_ar: 'فقرة نصية',
          correct_en: 'Paragraph',
          distractors_ar: ['موسوعة كاملة', 'قاموس لغوي', 'حبر الكتابة'],
          distractors_en: ['Full encyclopedia', 'Dictionary', 'Writing ink'],
          exp_ar: 'تسلسل بنائي لغوي: الحروف تشكل كلمات، والكلمات تشكل جملاً، ومجموعة الجمل المترابطة تشكل الفقرة مباشرة قبل الفصل والكتاب.',
          exp_en: 'Linguistic hierarchy: letters -> words -> sentences -> paragraphs.'
        },
        {
          seq_ar: ['خلية حية', 'نسيج عضوي', 'عضو حيوي'],
          seq_en: ['Biological Cell', 'Organic Tissue', 'Vital Organ'],
          correct_ar: 'جهاز عضوي متكامل',
          correct_en: 'Organ System',
          distractors_ar: ['بروتين كيميائي', 'خلية دم مفردة', 'غشاء خلوي'],
          distractors_en: ['Chemical protein', 'Single blood cell', 'Cell membrane'],
          exp_ar: 'المستوى البيولوجي التكاملي التالي: الخلايا تكون أنسجة، والأنسجة تكون أعضاء، ومجموعة الأعضاء المتكاملة تكون جهازًا حيوياً.',
          exp_en: 'Biological organization hierarchy: cells -> tissues -> organs -> organ systems.'
        },
        {
          seq_ar: ['بايت Byte', 'كيلوبايت KB', 'ميجابايت MB'],
          seq_en: ['Byte', 'Kilobyte (KB)', 'Megabyte (MB)'],
          correct_ar: 'جيجابايت (GB)',
          correct_en: 'Gigabyte (GB)',
          distractors_ar: ['بت Bit', 'نانوبايت', 'تيرابايت TB'],
          distractors_en: ['Bit', 'Nanobyte', 'Terabyte (TB)'],
          exp_ar: 'تدرج سعات الذاكرة الرقمية بالمضاعفة المعيارية (1024): بايت ثم كيلو ثم ميجا ثم جيجا مباشرة قبل التيرابايت.',
          exp_en: 'Digital capacity hierarchy: Byte -> KB -> MB -> GB -> TB.'
        },
        {
          seq_ar: ['ثانية', 'دقيقة', 'ساعة'],
          seq_en: ['Second', 'Minute', 'Hour'],
          correct_ar: 'يوم كامل',
          correct_en: 'Day',
          distractors_ar: ['سنة كبيسة', 'قرن زمني', 'شهر قمري'],
          distractors_en: ['Leap year', 'Century', 'Lunar month'],
          exp_ar: 'وحدة الزمن الهرمية التراكمية المباشرة التالية لـ 24 ساعة هي اليوم الكامل.',
          exp_en: 'Direct cumulative chronological unit following 24 hours is a day.'
        },
        {
          seq_ar: ['نقطة هندسية (أبعاد: 0)', 'خط مستقيم (أبعاد: 1)', 'سطح مستوٍ (أبعاد: 2)'],
          seq_en: ['Point (0D)', 'Line (1D)', 'Plane (2D)'],
          correct_ar: 'مجسم فراغي (أبعاد: 3)',
          correct_en: 'Spatial Solid (3D)',
          distractors_ar: ['زاوية هندسية', 'نقطة تقاطع', 'دائرة مستوية'],
          distractors_en: ['Geometric angle', 'Intersection', 'Planar circle'],
          exp_ar: 'التسلسل البعدي الرياضي: بعد صفري (نقطة)، بعد أحادي (خط)، بعد ثنائي (مستوٍ)، بعد ثلاثي (مجسم فراغي).',
          exp_en: 'Geometric dimensional progression: 0D -> 1D -> 2D -> 3D solid.'
        },
        {
          seq_ar: ['ذرة مفردة', 'جزيء كيميائي', 'مركب عضوي معقد'],
          seq_en: ['Single Atom', 'Chemical Molecule', 'Complex Polymer'],
          correct_ar: 'عضية خلوية Organelle',
          correct_en: 'Cell Organelle',
          distractors_ar: ['كوارك نووي', 'إلكترون', 'عنصر بسيط'],
          distractors_en: ['Nuclear quark', 'Electron', 'Simple element'],
          exp_ar: 'التدرج التركيبي من المستوى دون الجزيئي إلى التراكيب الخلوية الحيوية.',
          exp_en: 'Structural complexity scaling from molecular compounds to cellular organelles.'
        },
        {
          seq_ar: ['مستوى المعاملة الفردية', 'مستوى الفريق المحلي', 'مستوى القسم الإداري'],
          seq_en: ['Individual level', 'Team level', 'Departmental level'],
          correct_ar: 'مستوى المؤسسة ككل Organization',
          correct_en: 'Organization-wide level',
          distractors_ar: ['مستوى العميل الواحد', 'مكتب الموظف', 'مهمة عمل'],
          distractors_en: ['Single client', 'Desk space', 'Single task'],
          exp_ar: 'التدرج الهيكلي الإداري من الوحدة الصغرى إلى النطاق المؤسسي الشامل.',
          exp_en: 'Organizational hierarchy scaling from individual to enterprise-wide scope.'
        },
        {
          seq_ar: ['ملمتر (mm)', 'سنتيمتر (cm)', 'ديسيمتر (dm)'],
          seq_en: ['Millimeter (mm)', 'Centimeter (cm)', 'Decimeter (dm)'],
          correct_ar: 'متر (m)',
          correct_en: 'Meter (m)',
          distractors_ar: ['كيلومتر (km)', 'ميكرومتر', 'ميل بحري'],
          distractors_en: ['Kilometer (km)', 'Micrometer', 'Nautical mile'],
          exp_ar: 'المستوى المتري المتدرج التالي بزيادة معامل 10x هو المتر الكامل.',
          exp_en: 'Direct metric decade scale increment: mm -> cm -> dm -> meter.'
        },
        {
          seq_ar: ['شارع محلي', 'حي سكني', 'مدينة'],
          seq_en: ['Street', 'Neighborhood', 'City'],
          correct_ar: 'محافظة / إقليم',
          correct_en: 'Province / State',
          distractors_ar: ['قارة جغرافية', 'رصيف المشاة', 'مبنى سكني'],
          distractors_en: ['Continent', 'Pavement', 'Apartment'],
          exp_ar: 'التدرج الجغرافي الإداري: الشارع جزء من الحي، والحي جزء من المدينة، والمدينة جزء من المحافظة.',
          exp_en: 'Geographic administrative hierarchy from street unit to regional province.'
        },
        {
          seq_ar: ['مشهد مفرد', 'فصل مسرحي', 'مسرحية كاملة'],
          seq_en: ['Scene', 'Act', 'Play'],
          correct_ar: 'ثلاثية مسرحية متكاملة Trilogy',
          correct_en: 'Theatrical Trilogy',
          distractors_ar: ['حوار لشخصين', 'ستار المسرح', 'تذكرة عرض'],
          distractors_en: ['Dialogue line', 'Stage curtain', 'Ticket'],
          exp_ar: 'المستوى البنائي الدرامي التالي بعد العمل المسرحي المستقل هو الثلاثية أو السلسلة.',
          exp_en: 'Dramatic composition hierarchy extending beyond a single play into a cycle.'
        },
        {
          seq_ar: ['صوت لغوي (فونيم)', 'مقطع صوتي (سيلابل)', 'كلمة لغوية'],
          seq_en: ['Phoneme', 'Syllable', 'Word'],
          correct_ar: 'عبارة لغوية Phrase',
          correct_en: 'Grammatical Phrase',
          distractors_ar: ['أبجدية كاملة', 'حنجرة صوتية', 'حرف مفرد'],
          distractors_en: ['Full alphabet', 'Vocal cord', 'Single letter'],
          exp_ar: 'التسلسل اللغوي الصوتي والتركيبي: الفونيم يشكل مقطعًا، والمقاطع تشكل كلمات، والكلمات تشكل عبارات.',
          exp_en: 'Linguistic structural ladder: phonemes form syllables, forming words, forming phrases.'
        },
        {
          seq_ar: ['مستوى البت المنطقي Bit', 'بوابة منطقية Logic Gate', 'دارة متكاملة Integrated Circuit'],
          seq_en: ['Logical Bit', 'Logic Gate', 'Integrated Circuit'],
          correct_ar: 'معالج دقيق Microprocessor',
          correct_en: 'Microprocessor Unit',
          distractors_ar: ['شاحن كهربائي', 'مفتاح ضغط', 'سلك نحاسي'],
          distractors_en: ['Power charger', 'Push switch', 'Copper wire'],
          exp_ar: 'التدرج المعماري للحواسيب: البتات تعالجها بوابات، والبوابات تبني دارات، والدارات تتكامل لتشكل المعالج.',
          exp_en: 'Computer architecture hierarchy from binary logic gates to microprocessors.'
        },
        {
          seq_ar: ['فرد مفرد', 'عشيرة / جماعة محلية', 'مجتمع محلي'],
          seq_en: ['Individual', 'Clan / Local group', 'Community'],
          correct_ar: 'أمة / مجتمع مدني كامل Nation',
          correct_en: 'Nation / Society',
          distractors_ar: ['منزل الأسرة', 'شخص واحد', 'صديق مقرب'],
          distractors_en: ['Family house', 'One person', 'Close friend'],
          exp_ar: 'التدرج السوسيولوجي من الفرد نحو المجتمع الشامل للأمة.',
          exp_en: 'Sociological aggregation hierarchy from individual organism to whole society.'
        },
        {
          seq_ar: ['كوكب', 'مجموعة نجمية شمسية', 'مجرة حلزونية'],
          seq_en: ['Planet', 'Solar System', 'Spiral Galaxy'],
          correct_ar: 'عنقود مجري Galaxy Cluster',
          correct_en: 'Galaxy Cluster',
          distractors_ar: ['نيزك صغير', 'قمر فضائي', 'غبار كوني'],
          distractors_en: ['Small meteor', 'Moon satellite', 'Cosmic dust'],
          exp_ar: 'التدرج الكوني الفلكي من الكواكب إلى النجوم ثم المجرات فالعناقيد المجرية الكبرى.',
          exp_en: 'Astronomical scale hierarchy: planets -> systems -> galaxies -> clusters.'
        }
      ];

      const item = prng.pick(hierarchies);
      const allChoices = prng.shuffle([
        { ar: item.correct_ar, en: item.correct_en, isCorrect: true },
        { ar: item.distractors_ar[0], en: item.distractors_en[0], isCorrect: false },
        { ar: item.distractors_ar[1], en: item.distractors_en[1], isCorrect: false },
        { ar: item.distractors_ar[2], en: item.distractors_en[2], isCorrect: false }
      ]);

      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `ما هو المستوى البنائي التالي مباشرة في هذا التسلسل الهرمي المنطقي: (${item.seq_ar.join(' ← ')}) ← [ ؟ ]؟`,
        questionText_en: `What is the immediate next structural tier in this hierarchical progression: (${item.seq_en.join(' → ')}) → [ ? ]?`,
        stimulusHtml: '',
        options,
        correctIndex,
        explanation_ar: item.exp_ar,
        explanation_en: item.exp_en
      };
    }
  },

  // 7. Dual Underlying Trait Connective (استنباط الخاصية المشتركة الجوهرية)
  {
    id: 'verbal_dual_underlying_trait',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 35,
    generate(prng, difficulty = 2) {
      const questions = [
        {
          items_ar: ['الذهب', 'الفضة', 'البلاتين'],
          items_en: ['Gold', 'Silver', 'Platinum'],
          correct_ar: 'النحاس (فلز نقي فائق التوصيل الكهربائي وقابل للتشكيل)',
          correct_en: 'Copper (Malleable pure metal conductor)',
          distractors_ar: [
            'الماس (بلورة كربونية صلبة وعازلة كهربائياً)',
            'الزجاج (مادة سيليكاتية لابلورية هشة)',
            'الخشب (مادة عضوية سليلوزية غير معدنية)'
          ],
          distractors_en: [
            'Diamond (Carbon crystal insulator)',
            'Glass (Amorphous silicate solid)',
            'Wood (Organic cellulose non-metal)'
          ],
          exp_ar: 'الخاصية المشتركة الجوهرية: جميعها فلزات نقية ذات بريق معدني وتوصيل كهربائي وحراري عالٍ وقابلية للسحب والطرق.',
          exp_en: 'Intrinsic common trait: all members are pure ductile elemental metals with high conductivity.'
        },
        {
          items_ar: ['المربع', 'المستطيل', 'المعين'],
          items_en: ['Square', 'Rectangle', 'Rhombus'],
          correct_ar: 'متوازي الأضلاع (كل ضلعين متقابلين متوازيان ومتطابقان)',
          correct_en: 'Parallelogram (Opposite sides parallel & congruent)',
          distractors_ar: [
            'شبه المنحرف (ضلعان متوازيان فقط والآخران غير متوازيين)',
            'المثلث متطابق الضلعين (ثلاثة أضلاع فقط)',
            'المخمس المنتظم (خمسة أضلاع)'
          ],
          distractors_en: [
            'Trapezoid (Only two parallel sides)',
            'Isosceles Triangle (Three sides only)',
            'Regular Pentagon (Five sides)'
          ],
          exp_ar: 'الخاصية المشتركة: جميع عناصر المجموعة تحقق شروط متوازي الأضلاع الرباعي بالكامل.',
          exp_en: 'Intrinsic common trait: all items belong to the class of quadrilaterals with opposite parallel sides.'
        },
        {
          items_ar: ['الماء النقي (H2O)', 'غاز الميثان (CH4)', 'ثاني أكسيد الكربون (CO2)'],
          items_en: ['Water (H2O)', 'Methane (CH4)', 'Carbon Dioxide (CO2)'],
          correct_ar: 'الأمونيا (NH3 - مركب كيميائي تساهمي ناتج عن اتحاد عناصر)',
          correct_en: 'Ammonia (NH3 - pure covalent chemical compound)',
          distractors_ar: [
            'الهواء الجوي (مخلوط غازي غير متحد كيميائياً)',
            'عنصر الأكسجين النقي O2 (عنصر مفرد وليس مركباً)',
            'سبيكة الفولاذ (محلول صلب ومخلوط فلزي)'
          ],
          distractors_en: [
            'Atmospheric air (Physical gas mixture)',
            'Pure Oxygen O2 (Elemental diatomic gas, not compound)',
            'Steel alloy (Physical solid solution mixture)'
          ],
          exp_ar: 'الخاصية الجوهرية: جميعها مركبات كيميائية نقية تتحد ذراتها بروابط تساهمية محددة، وليست مخاليط.',
          exp_en: 'Intrinsic trait: all are pure chemical compounds formed by covalent bonds, not mixtures.'
        },
        {
          items_ar: ['السرعة', 'القوة', 'عزم الدوران'],
          items_en: ['Velocity', 'Force', 'Torque'],
          correct_ar: 'التسارع (كمية فيزيائية متجهة تلزم تحديد الاتجاه مع المقدار)',
          correct_en: 'Acceleration (Vector quantity requiring direction)',
          distractors_ar: [
            'درجة الحرارة (كمية قياسية بلا اتجاه)',
            'حجم المكعب (كمية قياسية محددة بالمقدار فقط)',
            'الكتلة (خاصية قياسية جوهرية لا اتجاه لها)'
          ],
          distractors_en: [
            'Temperature (Scalar quantity with no direction)',
            'Volume (Scalar measurement with magnitude only)',
            'Mass (Intrinsic scalar invariant without direction)'
          ],
          exp_ar: 'الخاصية المشتركة: جميعها كميات متجهة (Vectors) لا تكتمل فيزيائياً إلا بمقدار واتجاه محدد.',
          exp_en: 'Intrinsic trait: all items are vector physical quantities requiring direction in space.'
        },
        {
          items_ar: ['النسر', 'الصقر', 'العقاب'],
          items_en: ['Eagle', 'Falcon', 'Hawk'],
          correct_ar: 'البومة (طائر جارح لاحم ذو مخالب حادة ورؤية استثنائية)',
          correct_en: 'Owl (Carnivorous raptor with talons & binocular vision)',
          distractors_ar: [
            'الببغاء (طائر نباتي يتغذى على الحبوب والثمار)',
            'الحمام (طائر داجن حبّي)',
            'النعامة (طائر غير قادر على الطيران)'
          ],
          distractors_en: [
            'Parrot (Frugivorous/seed-eating non-predatory bird)',
            'Pigeon (Granivorous bird)',
            'Ostrich (Flightless terrestrial bird)'
          ],
          exp_ar: 'الخاصية المشتركة: جميعها طيور جارحة لاحمة (Raptors) تعتمد على الصيد بالمخالب والرؤية الدقيقة.',
          exp_en: 'Intrinsic trait: all items belong to carnivorous birds of prey equipped with hunting talons.'
        },
        {
          items_ar: ['المريخ', 'المشتري', 'زحل'],
          items_en: ['Mars', 'Jupiter', 'Saturn'],
          correct_ar: 'الزهرة (كوكب يدور حول نجم محدد ولا يولد طاقة بالاندماج)',
          correct_en: 'Venus (Planet orbiting a star, no nuclear fusion)',
          distractors_ar: [
            'الشمس (نجم مضيء ذاتياً بالاندماج النووي)',
            'القمر (تابع فلكي يدور حول كوكب وليس الشمس مباشرة)',
            'سديم الجبار (سحابة غازية وغبار كوني)'
          ],
          distractors_en: [
            'Sun (Self-luminous star via nuclear fusion)',
            'Moon (Natural satellite orbiting a planet)',
            'Orion Nebula (Diffuse cosmic dust cloud)'
          ],
          exp_ar: 'الخاصية المشتركة: جميعها كواكب تابعة تدور في مدارات حول الشمس وتستمد ضوءها منها.',
          exp_en: 'Intrinsic trait: all members are planets orbiting the host star without intrinsic fusion.'
        },
        {
          items_ar: ['الراديو', 'الأشعة تحت الحمراء', 'الضوء المرئي'],
          items_en: ['Radio Waves', 'Infrared', 'Visible Light'],
          correct_ar: 'أشعة إكس X-Rays (موجات كهرومغناطيسية تنتقل بسرعة الضوء في الفراغ)',
          correct_en: 'X-Rays (Electromagnetic radiation propagating at c)',
          distractors_ar: [
            'موجات الصوت (موجات ميكانيكية طولية تحتاج وسطاً مادياً)',
            'موجات تسونامي في المحيط (موجات مائية ميكانيكية)',
            'التيار الكهربائي في السلك (حركة شحنات إلكترونية)'
          ],
          distractors_en: [
            'Sound waves (Mechanical longitudinal waves)',
            'Ocean tsunami waves (Mechanical water disturbance)',
            'Electric wire current (Drift of physical electrons)'
          ],
          exp_ar: 'الخاصية المشتركة: جميعها أشكال مختلفة من الإشعاع الكهرومغناطيسي تنتشر في الفراغ بنفس السرعة.',
          exp_en: 'Intrinsic trait: all are bands of the electromagnetic spectrum traveling at light speed.'
        },
        {
          items_ar: ['الخلايا الجذعية', 'خلايا الدم البيضاء', 'الخلايا العصبية'],
          items_en: ['Stem Cells', 'White Blood Cells', 'Neurons'],
          correct_ar: 'الخلايا العضلية (خلايا حية حقيقية النواة تتبع الكائنات متعددة الخلايا)',
          correct_en: 'Muscle Cells (Living eukaryotic animal cells)',
          distractors_ar: [
            'فيروس الإنفلونزا (كيان لافلوي غير حي بذاته)',
            'جزيء الهيموجلوبين (بروتين كيميائي وليس خلية)',
            'بلورة الملح (مادة غير عضوية جماد)'
          ],
          distractors_en: [
            'Influenza virus (Non-cellular acellular entity)',
            'Hemoglobin molecule (Chemical protein, not cell)',
            'Salt crystal (Inorganic non-living solid)'
          ],
          exp_ar: 'الخاصية المشتركة: جميعها وحدات خلوية حية حقيقية النواة متخصصة داخل جسم الكائن الحي.',
          exp_en: 'Intrinsic trait: all are specialized eukaryotic biological living cells.'
        },
        {
          items_ar: ['العدد 2', 'العدد 3', 'العدد 5'],
          items_en: ['Number 2', 'Number 3', 'Number 5'],
          correct_ar: 'العدد 7 (عدد أولي موجب لا يقبل القسمة إلا على 1 ونفسه)',
          correct_en: 'Number 7 (Prime integer divisible only by 1 and self)',
          distractors_ar: [
            'العدد 9 (يقبل القسمة على 3 فليس أولياً)',
            'العدد 4 (عدد زوجي يقبل القسمة على 2)',
            'العدد 1 (ليس عدداً أولياً حسب التعريف الرياضي)'
          ],
          distractors_en: [
            'Number 9 (Composite number divisible by 3)',
            'Number 4 (Even composite number divisible by 2)',
            'Number 1 (Not prime by mathematical convention)'
          ],
          exp_ar: 'الخاصية الرياضية المشتركة: جميع عناصر المجموعة أعداد أولية.',
          exp_en: 'Intrinsic mathematical property: all members belong to prime numbers.'
        },
        {
          items_ar: ['الجاذبية', 'القوة الكهرومغناطيسية', 'القوة النووية القوية'],
          items_en: ['Gravity', 'Electromagnetism', 'Strong Nuclear Force'],
          correct_ar: 'القوة النووية الضعيفة (إحدى القوى الفيزيائية الأساسية الأربع في الكون)',
          correct_en: 'Weak Nuclear Force (One of 4 fundamental forces of nature)',
          distractors_ar: [
            'قوة الشد في الحبل (قوة ميكانيكية فرعية مجمعة)',
            'قوة الاحتكاك السطحي (أثر تلامس مجهري مشتق)',
            'قوة دفع الرياح (ديناميكا موائع ميكانيكية)'
          ],
          distractors_en: [
            'Rope tension force (Macroscopic derived contact force)',
            'Friction force (Microscopic derived electromagnetic effect)',
            'Wind drag force (Fluid mechanics bulk interaction)'
          ],
          exp_ar: 'الخاصية المشتركة: تمثل القوى الأساسية الأربع غير المشتقة في النموذج المعياري للفيزياء.',
          exp_en: 'Intrinsic trait: fundamental non-derived forces of the Standard Model in physics.'
        },
        {
          items_ar: ['الأكسجين O2', 'النيتروجين N2', 'الهيدروجين H2'],
          items_en: ['Oxygen O2', 'Nitrogen N2', 'Hydrogen H2'],
          correct_ar: 'الكلور Cl2 (عنصر غازي نقي ثنائي الذرة في الظروف المعيارية)',
          correct_en: 'Chlorine Cl2 (Diatomic pure elemental gas at STP)',
          distractors_ar: [
            'أول أكسيد الكربون CO (مركب من عنصرين مختلفين)',
            'الهيليوم He (غاز أحادي الذرة Monatomic)',
            'الكبريت الصلب S8 (مادة صلبة في الظروف العادية)'
          ],
          distractors_en: [
            'Carbon Monoxide CO (Heteronuclear compound of 2 elements)',
            'Helium He (Monatomic noble gas)',
            'Solid Sulfur S8 (Solid octatomic lattice at STP)'
          ],
          exp_ar: 'الخاصية المشتركة: جميعها جزيئات غازية ثنائية الذرة لعنصر واحد متجانس (Diatomic elements).',
          exp_en: 'Intrinsic trait: all are homonuclear diatomic elemental gases under standard conditions.'
        },
        {
          items_ar: ['البرمجة بلغة C', 'البرمجة بلغة Python', 'البرمجة بلغة Rust'],
          items_en: ['C Language', 'Python Language', 'Rust Language'],
          correct_ar: 'لغة Java (لغة برمجة متكاملة تورينغية لكتابة البرمجيات)',
          correct_en: 'Java Language (Turing-complete general programming language)',
          distractors_ar: [
            'لغة HTML (لغة توصيف وهيكلة مستندات وليست لغة برمجة خوارزمية)',
            'نظام Linux (نظام تشغيل وليس لغة برمجة)',
            'بروتوكول HTTP (بروتوكول شبكي لنقل البيانات)'
          ],
          distractors_en: [
            'HTML (Document markup formatting language, not Turing-complete)',
            'Linux (Operating system platform, not programming language)',
            'HTTP Protocol (Network data transfer protocol)'
          ],
          exp_ar: 'الخاصية المشتركة: جميعها لغات برمجة خوارزمية كاملة تملك القدرة الحسابية التورينغية.',
          exp_en: 'Intrinsic trait: all are Turing-complete algorithmic general-purpose programming languages.'
        },
        {
          items_ar: ['الأرجواني', 'البرتقالي', 'الأخضر'],
          items_en: ['Purple', 'Orange', 'Green'],
          correct_ar: 'البني (لون ثانوي مركب ينتج عن مزج الألوان الأساسية)',
          correct_en: 'Brown (Composite secondary/tertiary mixed color)',
          distractors_ar: [
            'الأحمر (لون أساسي أولي لا ينتج عن مزج)',
            'الأزرق (لون أولي خالص)',
            'الأصفر (لون أولي نقي)'
          ],
          distractors_en: [
            'Red (Primary foundational color, not mixed)',
            'Blue (Pure primary color)',
            'Yellow (Pure primary color in traditional color theory)'
          ],
          exp_ar: 'الخاصية المشتركة: جميعها ألوان ثانوية أو مركبة تنتج من دمج الألوان الأولية.',
          exp_en: 'Intrinsic trait: all items are composite mixed secondary colors derived from primaries.'
        },
        {
          items_ar: ['البيانو', 'القيثارة', 'التشيلو Cello'],
          items_en: ['Piano', 'Harp', 'Cello'],
          correct_ar: 'الكمان (آلة موسيقية وترية تولد النغمات باهتزاز الأوتار المشدودة)',
          correct_en: 'Violin (Stringed instrument generating sound via strings)',
          distractors_ar: [
            'الناي والناي الخشبي (آلة نفخ هوائية)',
            'الطبلة الإيقاعية (آلة قرع جلدية)',
            'الميكروفون (محول طاقة صوتي إلكتروني)'
          ],
          distractors_en: [
            'Flute (Aerophone wind instrument)',
            'Snare Drum (Membranophone percussion instrument)',
            'Microphone (Electronic acoustic transducer)'
          ],
          exp_ar: 'الخاصية المشتركة: جميعها تصنف كآلات وترية (Chordophones) تعتمد على اهتزاز الأوتار.',
          exp_en: 'Intrinsic trait: all are chordophones producing musical sound through vibrating strings.'
        }
      ];

      const item = prng.pick(questions);
      const allChoices = prng.shuffle([
        { ar: item.correct_ar, en: item.correct_en, isCorrect: true },
        { ar: item.distractors_ar[0], en: item.distractors_en[0], isCorrect: false },
        { ar: item.distractors_ar[1], en: item.distractors_en[1], isCorrect: false },
        { ar: item.distractors_ar[2], en: item.distractors_en[2], isCorrect: false }
      ]);

      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `إذا كانت الكلمات: (${item.items_ar.join(' ، ')}) تشترك في خاصية جوهرية محددة، أي من الخيارات التالية ينتمي لنفس المجموعة؟`,
        questionText_en: `If the terms: (${item.items_en.join(', ')}) share one strict foundational trait, which option also belongs?`,
        stimulusHtml: '',
        options,
        correctIndex,
        explanation_ar: item.exp_ar,
        explanation_en: item.exp_en
      };
    }
  },

  // 8. Relational Transitive Ordering & Contextual Deduction (الاستنتاج التراتبي المتعدي)
  {
    id: 'verbal_contextual_deduction',
    domain: 'verbal_reasoning',
    supportedDifficulties: [2, 3],
    expectedTimeSeconds: 40,
    generate(prng, difficulty = 2) {
      const puzzles = [
        {
          text_ar: 'حصل ماجد على درجة أعلى من خالد في الرياضيات، بينما حصل فهد على درجة مساوية لماجد، وحصل زياد على درجة أدنى من خالد.',
          text_en: 'Majid scored higher than Khalid in math, Fahad scored equal to Majid, and Ziyad scored lower than Khalid.',
          correct_ar: 'فهد حصل على درجة أعلى من زياد بالضرورة.',
          correct_en: 'Fahad necessarily scored higher than Ziyad.',
          distractors_ar: [
            'خالد حصل على أعلى درجة بين الجميع.',
            'زياد وفهد متساويان في النتيجة.',
            'ماجد حصل على درجة أقل من خالد.'
          ],
          distractors_en: [
            'Khalid scored the highest overall.',
            'Ziyad and Fahad scored equally.',
            'Majid scored lower than Khalid.'
          ],
          exp_ar: 'الترتيب التنازلي التام للدرجات: (ماجد = فهد) > خالد > زياد. وبالتالي فهد بالضرورة أعلى من زياد.',
          exp_en: 'Transitive chain: (Majid = Fahad) > Khalid > Ziyad. Thus Fahad must strictly exceed Ziyad.'
        },
        {
          text_ar: 'البرج (أ) أطول من البرج (ب)، والبرج (ج) أقصر من البرج (ب)، والبرج (د) أطول من البرج (أ).',
          text_en: 'Tower A is taller than Tower B, Tower C is shorter than Tower B, and Tower D is taller than Tower A.',
          correct_ar: 'البرج (ج) هو الأقصر على الإطلاق بين الأبراج الأربعة.',
          correct_en: 'Tower C is strictly the shortest of all four towers.',
          distractors_ar: [
            'البرج (ب) هو أطول الأبراج.',
            'البرج (أ) أقصر من البرج (ج).',
            'البرج (د) أقصر من البرج (ب).'
          ],
          distractors_en: [
            'Tower B is the tallest tower.',
            'Tower A is shorter than Tower C.',
            'Tower D is shorter than Tower B.'
          ],
          exp_ar: 'الترتيب التنازلي للأطوال: د > أ > ب > ج. إذن البرج (ج) هو الأقصر حتمًا.',
          exp_en: 'Descending height chain: D > A > B > C. Therefore C is strictly the shortest.'
        },
        {
          text_ar: 'العداء عمر أسرع من طارق، وطارق أسرع من سامي، بينما وصل بلال في المركز الأول قبل الجميع.',
          text_en: 'Runner Omar is faster than Tariq, Tariq is faster than Sami, while Bilal finished first ahead of everyone.',
          correct_ar: 'عمر أسرع من سامي بالضرورة المنطقية.',
          correct_en: 'Omar is necessarily faster than Sami.',
          distractors_ar: [
            'سامي أسرع من بلال.',
            'طارق وصل قبل بلال.',
            'عمر كان الأبطأ في السباق.'
          ],
          distractors_en: [
            'Sami is faster than Bilal.',
            'Tariq finished ahead of Bilal.',
            'Omar was the slowest runner.'
          ],
          exp_ar: 'علاقة التعدي في السرعة: بلال > عمر > طارق > سامي. إذن عمر أسرع من سامي بالضرورة.',
          exp_en: 'Transitive speed ranking: Bilal > Omar > Tariq > Sami. Omar is strictly faster than Sami.'
        },
        {
          text_ar: 'الصندوق الأحمر أثقل من الصندوق الأزرق، والصندوق الأصفر مساوٍ في الوزن للأحمر، بينما الصندوق الأخضر أخف من الأزرق.',
          text_en: 'The Red crate is heavier than the Blue crate, Yellow crate weighs equal to Red, and Green crate is lighter than Blue.',
          correct_ar: 'الصندوق الأخضر هو الأخف وزنًا بين الصناديق الأربعة.',
          correct_en: 'The Green crate is the lightest of all four crates.',
          distractors_ar: [
            'الصندوق الأزرق هو الأثقل وزناً.',
            'الصندوق الأصفر أخف من الصندوق الأخضر.',
            'الصندوق الأحمر والأخضر متساويان.'
          ],
          distractors_en: [
            'The Blue crate is the heaviest.',
            'The Yellow crate is lighter than the Green crate.',
            'The Red and Green crates are equal in weight.'
          ],
          exp_ar: 'ترتيب الأوزان: (الأحمر = الأصفر) > الأزرق > الأخضر. إذن الأخضر هو الأخف حتمًا.',
          exp_en: 'Weight ordering: (Red = Yellow) > Blue > Green. Thus Green is strictly the lightest.'
        },
        {
          text_ar: 'المدينة (س) تقع شرق المدينة (ص)، والمدينة (ع) تقع شرق المدينة (س)، بينما المدينة (م) تقع غرب المدينة (ص).',
          text_en: 'City X lies east of City Y, City Z lies east of City X, while City M lies west of City Y.',
          correct_ar: 'المدينة (ع) هي أبعد المدن باتجاه الشرق.',
          correct_en: 'City Z is the easternmost of all four cities.',
          distractors_ar: [
            'المدينة (ص) تقع شرق المدينة (ع).',
            'المدينة (م) تقع في أقصى الشرق.',
            'المدينة (س) تقع غرب المدينة (م).'
          ],
          distractors_en: [
            'City Y lies east of City Z.',
            'City M is located in the furthest east.',
            'City X lies west of City M.'
          ],
          exp_ar: 'الترتيب الجغرافي من الغرب إلى الشرق: م ← ص ← س ← ع. إذن (ع) هي الأكثر شرقًا.',
          exp_en: 'Spatial alignment from west to east: M -> Y -> X -> Z. City Z is furthest east.'
        },
        {
          text_ar: 'تكلفة المنتج (أ) أعلى من المنتج (ب)، وتكلفة (ج) أقل من (ب)، وتكلفة (د) أعلى من (أ).',
          text_en: 'Product A costs more than Product B, Product C costs less than B, and Product D costs more than A.',
          correct_ar: 'المنتج (ج) هو الأقل تكلفة بين المنتجات الأربعة.',
          correct_en: 'Product C has strictly the lowest cost among all four.',
          distractors_ar: [
            'المنتج (ب) هو الأعلى سعراً.',
            'المنتج (د) أرخص من المنتج (ج).',
            'المنتج (أ) مساوٍ لسعر المنتج (ج).'
          ],
          distractors_en: [
            'Product B has the highest cost.',
            'Product D is cheaper than Product C.',
            'Product A has identical price to Product C.'
          ],
          exp_ar: 'ترتيب التكلفة تنازليًا: د > أ > ب > ج. إذن المنتج (ج) هو الأرخص على الإطلاق.',
          exp_en: 'Price order: D > A > B > C. Product C is strictly the least expensive.'
        },
        {
          text_ar: 'خزان المياه (1) سعته أكبر من (2)، وسعة (3) أصغر من (2)، بينما سعة (4) مساوية لسعة (1).',
          text_en: 'Water tank 1 has greater capacity than 2, tank 3 is smaller than 2, and tank 4 has capacity equal to 1.',
          correct_ar: 'الخزان (4) سعته أكبر من الخزان (3) حتمًا.',
          correct_en: 'Tank 4 necessarily has greater capacity than Tank 3.',
          distractors_ar: [
            'الخزان (2) هو الأكبر سعة بين الجميع.',
            'الخزان (3) أكبر سعة من الخزان (1).',
            'الخزان (4) والخزان (2) متساويان في السعة.'
          ],
          distractors_en: [
            'Tank 2 has the largest capacity overall.',
            'Tank 3 has greater capacity than Tank 1.',
            'Tank 4 and Tank 2 have identical capacity.'
          ],
          exp_ar: 'السعة تنازليًا: (1 = 4) > 2 > 3. إذن سعة 4 أكبر من 3 بالضرورة.',
          exp_en: 'Capacity order: (1 = 4) > 2 > 3. Thus Tank 4 is strictly larger than Tank 3.'
        },
        {
          text_ar: 'المشروع (س) استغرق وقتًا أطول من المشروع (ص)، والمشروع (ع) أنجز أسرع من (ص)، والمشروع (ط) استغرق نفس وقت (ع).',
          text_en: 'Project X took longer than Project Y, Project Z finished faster than Y, and Project T took equal time to Z.',
          correct_ar: 'المشروع (س) استغرق وقتًا أطول من المشروع (ط) بالضرورة.',
          correct_en: 'Project X necessarily took longer than Project T.',
          distractors_ar: [
            'المشروع (ص) هو الأسرع إنجازاً.',
            'المشروع (ط) استغرق وقتاً أطول من (س).',
            'جميع المشاريع استغرقت أوقاتاً متساوية.'
          ],
          distractors_en: [
            'Project Y was the fastest to complete.',
            'Project T took longer than Project X.',
            'All projects took identical durations.'
          ],
          exp_ar: 'ترتيب المدة الزمنية: س > ص > (ع = ط). إذن مدة س أطول من ط بالضرورة.',
          exp_en: 'Duration chain: X > Y > (Z = T). Project X strictly took more time than T.'
        },
        {
          text_ar: 'الرياضي حازم رفع وزناً أثقل من كريم، وكريم رفع وزناً أثقل من رائد، بينما وليد رفع وزناً مساوياً لحازم.',
          text_en: 'Athlete Hazem lifted heavier than Kareem, Kareem lifted heavier than Raed, and Waleed lifted equal to Hazem.',
          correct_ar: 'رائد رفع أخف وزن بين الرياضيين الأربعة.',
          correct_en: 'Raed lifted strictly the lightest weight among all four.',
          distractors_ar: [
            'كريم رفع أثقل وزن بين الجميع.',
            'رائد وحازم رفعا نفس الوزن.',
            'وليد رفع وزناً أقل من كريم.'
          ],
          distractors_en: [
            'Kareem lifted the heaviest weight.',
            'Raed and Hazem lifted equal weights.',
            'Waleed lifted less weight than Kareem.'
          ],
          exp_ar: 'ترتيب الأوزان المرفوعة: (حازم = وليد) > كريم > رائد. رائد هو الأقل حتمًا.',
          exp_en: 'Lifting ranking: (Hazem = Waleed) > Kareem > Raed. Raed is strictly the lowest.'
        },
        {
          text_ar: 'المادة (ك) درجة انصهارها أعلى من (ل)، ودرجة انصهار (م) أقل من (ل)، ودرجة انصهار (ن) أعلى من (ك).',
          text_en: 'Substance K has higher melting point than L, substance M melts lower than L, and substance N melts higher than K.',
          correct_ar: 'المادة (ن) تمتلك أعلى درجة انصهار بين المواد الأربع.',
          correct_en: 'Substance N possesses strictly the highest melting point of all four.',
          distractors_ar: [
            'المادة (م) هي الأعلى في درجة الانصهار.',
            'المادة (ل) درجة انصهارها أعلى من (ن).',
            'المادة (ك) و (م) متساويتان.'
          ],
          distractors_en: [
            'Substance M has the highest melting point.',
            'Substance L melts at higher temperature than N.',
            'Substances K and M have identical melting points.'
          ],
          exp_ar: 'الترتيب التنازلي لدرجات الانصهار: ن > ك > ل > م. إذن (ن) هي الأعلى حتمًا.',
          exp_en: 'Melting point chain: N > K > L > M. Substance N is strictly the highest.'
        },
        {
          text_ar: 'الموظف عادل أنجز مهاماً أكثر من ماجد، وماجد أنجز أكثر من سامي، بينما ياسر أنجز مهاماً مساوية لعادل.',
          text_en: 'Adel completed more tasks than Majid, Majid completed more than Sami, and Yasser completed equal tasks to Adel.',
          correct_ar: 'ياسر أنجز مهاماً أكثر من سامي بالضرورة.',
          correct_en: 'Yasser necessarily completed more tasks than Sami.',
          distractors_ar: [
            'سامي أنجز مهاماً أكثر من عادل.',
            'ماجد أنجز أكثر من ياسر.',
            'عادل هو الأقل إنجازاً للдиقّة.'
          ],
          distractors_en: [
            'Sami completed more tasks than Adel.',
            'Majid completed more tasks than Yasser.',
            'Adel completed the fewest tasks.'
          ],
          exp_ar: 'الترتيب: (عادل = ياسر) > ماجد > سامي. إذن إنجاز ياسر يفوق سامي حتمًا.',
          exp_en: 'Productivity ranking: (Adel = Yasser) > Majid > Sami. Yasser strictly exceeds Sami.'
        },
        {
          text_ar: 'السفينة (أ) سرعتها أكبر من السفينة (ب)، والسفينة (ج) سرعتها أقل من (ب)، بينما السفينة (د) تبحر بنفس سرعة (أ).',
          text_en: 'Ship A is faster than Ship B, Ship C is slower than B, and Ship D cruises at equal speed to A.',
          correct_ar: 'السفينة (ج) هي الأبطأ على الإطلاق بين السفن الأربع.',
          correct_en: 'Ship C is strictly the slowest of all four vessels.',
          distractors_ar: [
            'السفينة (ب) هي الأسرع بين الجميع.',
            'السفينة (ج) أسرع من السفينة (د).',
            'السفينة (أ) والسفينة (ب) متساويتان.'
          ],
          distractors_en: [
            'Ship B is the fastest overall.',
            'Ship C is faster than Ship D.',
            'Ships A and B cruise at identical speed.'
          ],
          exp_ar: 'ترتيب السرعات: (أ = د) > ب > ج. إذن السفينة (ج) هي الأبطأ حتمًا.',
          exp_en: 'Speed ranking: (A = D) > B > C. Ship C is strictly the slowest.'
        },
        {
          text_ar: 'في تصنيف الأمان: النظام (1) أكثر أمانًا من (2)، والنظام (3) أقل أمانًا من (2)، والنظام (4) يتطابق في أمانه مع (1).',
          text_en: 'In security rating: System 1 is more secure than 2, System 3 is less secure than 2, and System 4 matches System 1.',
          correct_ar: 'النظام (4) أكثر أمانًا من النظام (3) بالضرورة المنطقية.',
          correct_en: 'System 4 is necessarily more secure than System 3.',
          distractors_ar: [
            'النظام (2) هو الأكثر أماناً.',
            'النظام (3) أكثر أماناً من النظام (4).',
            'النظام (2) والنظام (4) متطابقان.'
          ],
          distractors_en: [
            'System 2 has the highest security rating.',
            'System 3 is more secure than System 4.',
            'Systems 2 and 4 have identical security.'
          ],
          exp_ar: 'ترتيب درجات الأمان: (1 = 4) > 2 > 3. إذن النظام 4 يتفوق على 3 حتمًا.',
          exp_en: 'Security ranking: (1 = 4) > 2 > 3. System 4 strictly outperforms System 3.'
        },
        {
          text_ar: 'الكتاب (س) يحوي صفحات أكثر من (ص)، والكتاب (ع) يحوي صفحات أقل من (ص)، بينما الكتاب (ل) يحوي صفحات أكثر من (س).',
          text_en: 'Book X contains more pages than Y, Book Z has fewer pages than Y, and Book L contains more pages than X.',
          correct_ar: 'الكتاب (ع) هو الأقل صفحات بين الكتب الأربعة.',
          correct_en: 'Book Z has strictly the fewest pages among all four.',
          distractors_ar: [
            'الكتاب (ص) هو الأكبر حجماً.',
            'الكتاب (ل) صفحاته أقل من الكتاب (ع).',
            'الكتاب (س) والكتاب (ع) متساويان.'
          ],
          distractors_en: [
            'Book Y has the largest page count.',
            'Book L has fewer pages than Book Z.',
            'Books X and Z have identical page counts.'
          ],
          exp_ar: 'ترتيب الصفحات تنازليًا: ل > س > ص > ع. إذن الكتاب (ع) هو الأقل حتمًا.',
          exp_en: 'Page count order: L > X > Y > Z. Book Z strictly has the fewest pages.'
        }
      ];

      const item = prng.pick(puzzles);
      const allChoices = prng.shuffle([
        { ar: item.correct_ar, en: item.correct_en, isCorrect: true },
        { ar: item.distractors_ar[0], en: item.distractors_en[0], isCorrect: false },
        { ar: item.distractors_ar[1], en: item.distractors_en[1], isCorrect: false },
        { ar: item.distractors_ar[2], en: item.distractors_en[2], isCorrect: false }
      ]);

      const correctIndex = allChoices.findIndex(c => c.isCorrect);

      const options = allChoices.map((c, idx) => ({
        id: String.fromCharCode(65 + idx),
        text_ar: c.ar,
        text_en: c.en
      }));

      return {
        templateId: this.id,
        difficulty,
        questionText_ar: `اقرأ المعطيات التراتبية بعناية: "${item.text_ar}" — أي من الاستنتاجات التالية صحيح بالضرورة المنطقية؟`,
        questionText_en: `Read the relational premises carefully: "${item.text_en}" — Which conclusion is strictly true by deductive logic?`,
        stimulusHtml: '',
        options,
        correctIndex,
        explanation_ar: item.exp_ar,
        explanation_en: item.exp_en
      };
    }
  }
];
